/* eslint-disable no-unused-vars, react-hooks/exhaustive-deps */
import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useNavigate, Navigate } from "react-router-dom";
import { useForm, useFieldArray, Form } from "react-hook-form";
import "./style.css";
import DocumentUploadForm from "../../components/DocumentUploadForm/index.js";
import SuccessSubmitModal from "../../components/SuccessSubmitModal/index.js";
import MissingFieldsAlert from "../../components/Fill-inAlert/index.js";
import InputMask from "../../components/InputMask/index.js";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { NumericFormat } from "react-number-format";
import FormReview from "../../components/Compos Validation/index.js";
import { TbDoorExit } from "react-icons/tb";
import { jwtDecode } from "jwt-decode";
import { usePermissoes } from "./../../../hooks/usePermissoes.js";
import { API_ENDPOINTS, API_BASE_URL } from "../../../js/api.js";
import { toDateInputValue } from "../../../js/dateHelpers.js";
import {
  buildAnexosObrigatoriosIds,
  buildAnexosObrigatoriosBodyPatch,
  parseAnexosObrigatoriosFromError,
  hydrateAnexosFilesFromPayload,
} from "../../js/anexosObrigatorios.js";
import ToastContainer from "../../components/Toast/ToastContainer.js";
import useToast from "../../hooks/useToast.js";
import useConfirm from "../../hooks/useConfirm.js";
import { Check, AlertCircle } from "lucide-react";

import {
  LINK_DOCUSIGN_FALLBACK,
  STATUS_LABELS,
  STATUS_CORES,
  schema,
  ANEXOS_SCHEMA_TCE,
  formatarDataBR,
  formatarTelefone,
  formatarCnpj,
  normalizeCPF,
  calcularMaxTerminoEstagio,
  horarioValido,
  horasDecimais,
  validarHorarios,
  DIAS_SEMANA,
  hasDuplicateCPF,
  isPositiveNumber,
} from "./utils.js";

const AguardandoEmpresa = () => {
  const navigate = useNavigate();

  // Detecta se quem chegou aqui é um estudante (acesso_autorizacao = 1).
  // Se for, mostramos uma mensagem mais adequada — não é "a empresa
  // precisa criar o TCE", e sim "seu usuário não foi vinculado a um TCE
  // nesta sessão, faça login novamente".
  let isEstudanteAqui = false;
  try {
    const u = JSON.parse(localStorage.getItem("user") || "null");
    isEstudanteAqui = Number(u?.acesso_autorizacao ?? 0) === 1;
  } catch (_) {
    isEstudanteAqui = false;
  }

  const recarregarLogin = () => {
    try {
      window.localStorage.clear();
      window.sessionStorage.clear();
    } catch (_) {}
    navigate("/", { replace: true });
  };

  if (isEstudanteAqui) {
    return (
      <div className="tce-container tce-py-5 tce-text-center">
        <h2 className="tce-h2">Nenhuma autorização vinculada</h2>
        <p
          className="tce-text-muted"
          style={{ maxWidth: "500px", margin: "1rem auto" }}
        >
          Não localizamos uma autorização ativa vinculada ao seu CPF nesta
          sessão.
          <br />
          <br />
          Faça login novamente para carregar sua autorização. Se o problema
          persistir, entre em contato com o CIEEMG.
        </p>

        <button
          type="button"
          className="tce-btn tce-btn-primary"
          onClick={recarregarLogin}
          style={{ marginTop: "1rem" }}
        >
          Fazer login novamente
        </button>
      </div>
    );
  }

  return (
    <div className="tce-container tce-py-5 tce-text-center">
      <h2 className="tce-h2">Aguardando Empresa</h2>
      <p
        className="tce-text-muted"
        style={{ maxWidth: "500px", margin: "1rem auto" }}
      >
        Você ainda não tem um TCE para preencher.
        <br />
        <br />A empresa precisa criar o TCE primeiro. Depois você poderá abrir
        aqui e preencher seus dados.
      </p>

      <button
        type="button"
        className="tce-btn tce-btn-secondary"
        onClick={() => navigate("/")}
        style={{ marginTop: "1rem" }}
      >
        Voltar
      </button>
    </div>
  );
};

const TCE = () => {
  const currentUser = JSON.parse(localStorage.getItem("user"));
  const navigate = useNavigate();
  const {
    register,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
  });
  const {
    toasts,
    error: toastError,
    success: toastSuccess,
    warning: toastWarning,
    removeToast,
  } = useToast();
  const { confirm, confirmDialog } = useConfirm();
  const [successModalData, setSuccessModalData] = useState({
    isOpen: false,
    tceId: null,
    role: "empresa",
  });
  const [missingFieldsAlert, setMissingFieldsAlert] = useState([]);
  const [showReview, setShowReview] = useState(false);
  const [reviewRole, setReviewRole] = useState("empresa");
  const bypassingReviewRef = useRef(false);

  const buildReviewData = () => {
    const cpfLimpo = (
      formData.cpf_estudante_estagiario ||
      formData.cpf_estudante ||
      ""
    ).replace(/\D/g, "");

    const empresaFields = [
      {
        label: "Origem do Candidato",
        value: formData.origem_candidato,
        complete: !!formData.origem_candidato,
      },
      {
        label: "Início do Estágio",
        value: formData.inicio_estagio,
        complete: !!formData.inicio_estagio,
      },
      {
        label: "Término do Estágio",
        value: formData.termino_estagio,
        complete: !!formData.termino_estagio,
      },
      {
        label: "Valor Bolsa Auxílio",
        value: formData.valor_bolsa_auxilio,
        complete:
          !!formData.valor_bolsa_auxilio &&
          formData.valor_bolsa_auxilio !== "R$ 0,00",
      },
      {
        label: "Auxílio Transporte",
        value: formData.valor_bolsa_transporte,
        complete:
          !!formData.valor_bolsa_transporte &&
          formData.valor_bolsa_transporte !== "R$ 0,00",
      },
    ];

    const estudanteFields = [
      {
        label: "Nome do Estudante",
        value: formData.nome_estagiario || formData.nome_completo,
        complete: !!(formData.nome_estagiario || formData.nome_completo),
      },
      {
        label: "CPF",
        value: cpfLimpo,
        complete: cpfLimpo.length === 11,
      },
    ];

    return [
      { title: "Dados do Estágio (Empresa)", fields: empresaFields },
      { title: "Dados do Estudante", fields: estudanteFields },
    ];
  };

  const handleConfirmSubmit = async () => {
    setShowReview(false);
    bypassingReviewRef.current = true;
    if (reviewRole === "estudante") {
      await submeterValidacaoEstudante();
    } else {
      await handleSubmit({ preventDefault: () => {} });
    }
  };

  const handleMissingFieldClick = (fieldId) => {
    setMissingFieldsAlert([]);
    setTimeout(() => {
      if (typeof fieldId === "string" && fieldId.startsWith("doc_")) {
        const el = document.getElementById(`file-input-${fieldId}`);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
          if (typeof el.focus === "function") {
            try {
              el.focus({ preventScroll: true });
            } catch (_) {
              el.focus();
            }
          }
        }
        return;
      }

      const el =
        document.getElementById(fieldId) ||
        document.querySelector(`[name="${fieldId}"]`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        if (typeof el.focus === "function") {
          try {
            el.focus({ preventScroll: true });
          } catch (_) {
            el.focus();
          }
        }
      }
    }, 150);
  };

  const getAnexosObrigatoriosFaltandoTce = () => {
    return ANEXOS_SCHEMA_TCE.filter(
      (doc) => doc.required && !anexosFiles[doc.id],
    );
  };

  const [formData, setFormData] = useState({
    origem_candidato: "",
    estudante_id: null,
    dt_nascimento_estudante: "",
    empresa: "",
    cnpj_cpf: "",
    telefones: "",
    email_empresa: "",
    nome_estagiario: "",
    cargo_estagiario: "",
    cpf_estagiario: "",
    email_estagiario: "",
    email_supervisor: "",
    formacao_escolar: "",
    telefone_estagiario: "",
    nome_telefone_estudante_aprovado: "",
    estudanteaprovadofoiindicadooucaptado: "",
    curso: "",
    email: "",
    inicio_estagio: "",
    termino_estagio: "",
    termino_estagio_autopreenchido: null,
    valor_bolsa_auxilio: "",
    valor_bolsa_transporte: "",
    local_estagio: "",
    local_estagio_editado: false,
    local_realizacao_atividades: "",
    atividades_desenvolvidas: "",
    observacoes1: "",
    observacoes2: "",
    maior_menor_idade: "",
    email_menor_idade: "",
    nome_menor_idade: "",
    telefone_menor_idade: "",
    cpf_menor_idade: "",
    cpf_estudante_estagiario: "",
    rg: "",
    escola: "",
    nome: "",
    nome_termo_compromisso: "",
    unidade: "",
    ano_periodo_serie: "",
    previsao_formatura: "",
    tipo_estagio: "",
    tipo_contrato: "",
    orientador_estagio: "",
    horario_fixo_ou_alternado: "",
    cargo: "",
    email_escola: "",
    cnpj_escola: "",
    nome_filial: "",
    cpfCnpj: "",
    telefones2: "",
    bairro: "",
    municipio: "",
    uf: "",
    emailContratos: "",
    emailFinanceiro: "",
    representadaPor: "",
    cargoRepresentante: "",
    emailRepresentante: "",
    responsavelSupervisor: "",
    cargoSupervisor: "",
    emailSupervisor: "",
  });

  const [authorized, setAuthorized] = useState(false);
  const [declaracaoEscolar, setDeclaracaoEscolar] = useState(null);
  const [error, setError] = useState("");
  const [cargaHoraria, setCargaHoraria] = useState(null);
  const [cargaHorariaSemanal, setCargaHorariaSemanal] = useState(0);
  const {
    permissao,
    podeEditarCampo,
    isEmpresa,
    isEstudante,
    isCieemg,
    tipoLabel,
  } = usePermissoes();
  const [documentoIdentificacao, setDocumentoIdentificacao] = useState(null);
  const [estudantesCieemg, setEstudantesCieemg] = useState([]);
  const [loadingEstudantes, setLoadingEstudantes] = useState(false);
  const [estudanteEncontrado, setEstudanteEncontrado] = useState(false);
  const [estudanteNaoEncontrado, setEstudanteNaoEncontrado] = useState(false);
  const [showConsentimento, setShowConsentimento] = useState(false);
  const [cienteSemCadastro, setCienteSemCadastro] = useState(false);
  const [cienteCadastro, setCienteCadastro] = useState(false);
  const [loadingCpf, setLoadingCpf] = useState(false);
  const [idadeCalculada, setIdadeCalculada] = useState(null);
  const inputRef = useRef();
  const [filiais, setFiliais] = useState([]);
  const [mostrarFiliais, setMostrarFiliais] = useState(false);
  const [filialLoading, setFilialLoading] = useState(false);
  const [localAtividade, setLocalAtividade] = useState("");
  const [unidadesCieemg, setUnidadesCieemg] = useState([]);
  const [dadosFilialSelecionada, setDadosFilialSelecionada] = useState(null);
  const [nivel, setNivel] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [files, setFiles] = useState(null);
  const [progress, setProgress] = useState(0);
  const [anexosFiles, setAnexosFiles] = useState({});
  const [anexosProgress, setAnexosProgress] = useState({});
  const [anexosStatus, setAnexosStatus] = useState({});
  const [anexosObrigatoriosDestacar, setAnexosObrigatoriosDestacar] =
    useState([]);
  const [tceIdState, setTceIdState] = useState(null);
  const [msg, setMsg] = useState(null);
  const [val, setVal] = useState("");
  const [uploadMessage, setUploadMessage] = useState("");
  const [uploadStatus, setUploadStatus] = useState("select");
  const [user, setUser] = useState({ name: "", email: "" });
  const [status, setStatus] = useState({ type: "", mensagem: "" });
  const [arquivoSupervisor, setArquivoSupervisor] = useState(null);
  const [arquivoSupervisorId, setArquivoSupervisorId] = useState(null);
  const inputSupervisorRef = useRef(null);
  const [horarios, setHorarios] = useState({ tipo: "", dias: [] });
  const [enderecoEmpresa, setEnderecoEmpresa] = useState(null);
  const [showObs1, setShowObs1] = useState(false);
  const [showObs2, setShowObs2] = useState(false);
  const [possuiIncompatibilidade, setPossuiIncompatibilidade] = useState(false);
  const [modoEdicao, setModoEdicao] = useState(false);
  const [tceId, setTceId] = useState(null);
  const [tceStatus, setTceStatus] = useState(null);
  const [tceHistorico, setTceHistorico] = useState([]);
  const [tipoUsuario, setTipoUsuario] = useState(null);
  const [tceIdCarregado, setTceIdCarregado] = useState(null);
  const [tceCarregado, setTceCarregado] = useState(null);
  const [historico, setHistorico] = useState([]);
  const [tcesEstudante, setTcesEstudante] = useState([]);
  const [tcesPendentesCieemg, setTcesPendentesCieemg] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [mostrarHistorico, setMostrarHistorico] = useState(false);
  const [horario, setHorario] = useState({
    tipo: "",
    diasSelecionados: [],
    fixo: {
      entrada: "",
      saida: "",
      intervaloInicio: "",
      intervaloFim: "",
    },
    alternado: {
      segunda: {
        entrada: "",
        saida: "",
        intervaloInicio: "",
        intervaloFim: "",
      },
      terca: { entrada: "", saida: "", intervaloInicio: "", intervaloFim: "" },
      quarta: { entrada: "", saida: "", intervaloInicio: "", intervaloFim: "" },
      quinta: { entrada: "", saida: "", intervaloInicio: "", intervaloFim: "" },
      sexta: { entrada: "", saida: "", intervaloInicio: "", intervaloFim: "" },
      sabado: { entrada: "", saida: "", intervaloInicio: "", intervaloFim: "" },
      domingo: {
        entrada: "",
        saida: "",
        intervaloInicio: "",
        intervaloFim: "",
      },
    },
  });

  useEffect(() => {
    const filialId = String(localAtividade || "").trim();

    // Estudante não tem permissão para listar/buscar filiais
    // (acesso_atual = 1; o GET /filiais/:id requer nível 4).
    // Se ele abriu um TCE onde o backend devolveu um "ID" no campo
    // local_realizacao_atividades (que pode ser um número de CPF/
    // código vindo errado), NÃO dispara o GET — apenas silencia o
    // localAtividade para evitar mais tentativas em cascata.
    if (isEstudante) {
      if (filialId) setLocalAtividade("");
      setDadosFilialSelecionada(null);
      return;
    }

    if (!filialId || filialId === "MATRIZ" || isNaN(Number(filialId))) {

      if (modoEdicao) {
        setDadosFilialSelecionada(null);
        return;
      }
      const camposFilial = [
        "nome_filial",
        "cpfCnpj",
        "telefones",
        "telefones2",
        "bairro",
        "municipio",
        "uf",
        "emailContratos",
        "emailFinanceiro",
        "representadaPor",
        "cargoRepresentante",
        "emailRepresentante",
        "responsavelSupervisor",
        "cargoSupervisor",
        "emailSupervisor",
      ];
      setFormData((prev) => {
        const next = { ...prev };
        camposFilial.forEach((c) => {
          next[c] = "";
        });
        return next;
      });
      setDadosFilialSelecionada(null);
      return;
    }

    let cancelled = false;

    const carregarFilial = async () => {
      setFilialLoading(true);
      try {
        const token = localStorage.getItem("token");
        const url = API_ENDPOINTS.filial_id(filialId);
        const response = await axios.get(url, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (cancelled) return;

        const dados = response.data || {};

        setDadosFilialSelecionada(dados);

        if (modoEdicao) {
          return;
        }

        const mapeamento = [
          ["nome_filial", dados.nome_filial],
          ["cpfCnpj", dados.cnpj],
          ["cepFilial", dados.cep],
          ["enderecoFilial", dados.endereco],
          ["telefones", dados.telefone_1],
          ["telefones2", dados.telefone_2],
          ["bairro", dados.bairro],
          ["municipio", dados.cidade],
          ["uf", dados.uf],
          ["emailContratos", dados.email_contratos],
          ["emailFinanceiro", dados.email_financeiro],
          ["representadaPor", dados.representante_nome],
          ["cargoRepresentante", dados.representante_cargo],
          ["emailRepresentante", dados.representante_email],
          ["responsavelSupervisor", dados.supervisor_nome],
          ["cargoSupervisor", dados.supervisor_cargo],
          ["emailSupervisor", dados.supervisor_email],
        ];

        setFormData((prev) => {
          const next = { ...prev };
          mapeamento.forEach(([campo, valor]) => {
            if (valor !== undefined && valor !== null) {
              next[campo] = valor;
            }
          });

          // Auto-preencher o campo "Local da realização do estágio" com o
          // endereço da filial selecionada (se ainda não foi editado
          // manualmente nesta sessão).
          const enderecoFilial = [
            dados.endereco,
            dados.numero ? `nº ${dados.numero}` : null,
            dados.bairro ? `- ${dados.bairro}` : null,
            dados.cidade && dados.uf
              ? `- ${dados.cidade}/${dados.uf}`
              : dados.cidade || dados.uf
                ? `- ${dados.cidade || dados.uf}`
                : null,
          ]
            .filter(Boolean)
            .join(", ");

          if (enderecoFilial && !next.local_estagio_editado) {
            next.local_estagio = enderecoFilial;
          }

          return next;
        });

        toastSuccess("Dados da filial carregados.");
      } catch (err) {
        if (cancelled) return;
        const status = err.response?.status;
        const backendMsg =
          (Array.isArray(err.response?.data?.errors) &&
            err.response.data.errors.join(", ")) ||
          err.response?.data?.message ||
          err.response?.data?.error ||
          err.message ||
          null;
        console.error(
          `[TCE][Filial] erro ao carregar filial id=${filialId} status=${status}:`,
          err.response?.data,
        );
        if (status === 404) {
          toastError(
            backendMsg
              ? `Filial não encontrada: ${backendMsg}`
              : "Filial não encontrada",
          );
        } else if (status >= 500) {
          toastError(
            backendMsg
              ? `Erro no servidor ao buscar filial: ${backendMsg}`
              : "Erro no servidor ao buscar filial",
          );
        } else if (status === 401 || status === 403) {
          // Estudante / perfis sem permissão não devem ver toast aqui:
          // é um cenário esperado (não deveriam ter aberto essa tela
          // para filiais). Empresa com sessão expirada precisa relogar.
          if (!isEstudante) {
            toastError(
              "Sessão expirada ou sem permissão. Faça login novamente.",
            );
          }
        } else {
          toastError(
            backendMsg
              ? `Erro ao carregar filial: ${backendMsg}`
              : "Erro ao carregar dados da filial",
          );
        }
      } finally {
        if (!cancelled) setFilialLoading(false);
      }
    };

    carregarFilial();
    return () => {
      cancelled = true;
    };
  }, [localAtividade, modoEdicao, toastError, toastSuccess]);

  useEffect(() => {
    const token = localStorage.getItem("token");

    if (!token) {
      window.location.href = "/";
      return;
    }

    try {
      const decoded = jwtDecode(token);

      const nivelAcesso = Number(decoded.acesso_autorizacao ?? 0);
      setNivel(nivelAcesso);

      setFormData((prev) => ({
        ...prev,
        empresa_id: decoded.empresa_id,
      }));

      const niveisPermitidos = [0, 1, 2, 3, 4];

      if (!niveisPermitidos.includes(nivelAcesso)) {
        window.location.href = "/sem-permissao";
        return;
      }

      setAuthorized(true);
    } catch (e) {
      window.location.href = "/";
      return;
    }

    const emojiRegex = /[\p{Extended_Pictographic}]/gu;

    const limparEmoji = function () {
      if (
        this.id === "cnpj-cpf" ||
        this.id === "tce-telefones" ||
        this.id === "tce_rg_estagiario" ||
        this.id === "tce-six-cpf" ||
        this.id === "ano-periodo-serie" ||
        this.id === "previsao-formatura" ||
        this.id === "tce-eight-cnpj" ||
        this.id === "inicio-estagio" ||
        this.id === "termino-estagio" ||
        this.id === "valor_bolsa_auxilio" ||
        this.id === "valor_bolsa_transporte" ||
        this.id === "tce-two-row-cpf"
      )
        return;

      if (this.value) {
        this.value = this.value.replace(emojiRegex, "");
      }
    };

    const handleInputValidation = (inputId, regex) => {
      if (
        inputId === "#cnpj-cpf" ||
        inputId === "#tce-telefones" ||
        inputId === "#tce_rg_estagiario" ||
        inputId === "#tce-six-cpf" ||
        inputId === "#ano-periodo-serie" ||
        inputId === "#tce-eight-cnpj"
      )
        return null;

      const input = document.querySelector(inputId);

      if (input) {
        const handlePaste = function () {
          const self = this;
          setTimeout(() => {
            if (!regex.test(self.value)) self.value = "";
          }, 10);
        };

        const handleKeyPress = function (e) {
          const char = String.fromCharCode(e.keyCode);
          if (!regex.test(char)) e.preventDefault();
        };

        input.addEventListener("paste", handlePaste);
        input.addEventListener("keypress", handleKeyPress);

        return () => {
          input.removeEventListener("paste", handlePaste);
          input.removeEventListener("keypress", handleKeyPress);
        };
      }
    };

    const carregarFiliais = async () => {
      try {
        const token = localStorage.getItem("token");

        const response = await axios.get(`${API_BASE_URL}/tca/filiais`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        console.log("[Filial TCE] response:", response.data);

        if (response.data?.possuiFiliais) {
          setFiliais(response.data.opcoes);
          setMostrarFiliais(true);
        }
      } catch (error) {
        console.error("[Filial TCE] erro ao carregar filiais:", error);
      }
    };

    const carregarUnidadesCieemg = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(`${API_BASE_URL}/admin-unidades`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setUnidadesCieemg(response.data || []);
      } catch (error) {
        // Silencia erros: sem unidades o auto-fill fica vazio (campo fica em branco).
        console.warn(
          "[TCE] Falha ao carregar /admin-unidades:",
          error?.response?.status || error?.message,
        );
      }
    };

    const carregarEnderecoEmpresa = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(
          `${API_BASE_URL}/tce/empresa-endereco`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        setEnderecoEmpresa(response.data);
      } catch (error) {
        console.warn(
          "[TCE] Erro ao carregar endereço da empresa:",
          error?.message,
        );
      }
    };

    const carregarDadosEmpresa = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(`${API_BASE_URL}/tce/dados-empresa`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.data) {
          setFormData((prev) => ({
            ...prev,
            empresa:
              response.data.razaosocial ||
              response.data.nome_empresa ||
              prev.empresa,
            cnpj_cpf: response.data.cnpj || prev.cnpj_cpf,
            telefones:
              response.data.telefone_contato ||
              response.data.telefone ||
              prev.telefones,
            email_empresa:
              response.data.email ||
              response.data.email_empresa ||
              response.data.email_contato ||
              prev.email_empresa,
            nome_termo_compromisso:
              response.data.nome_representante || prev.nome_termo_compromisso,
            cargo: response.data.cargo_representante || prev.cargo,
          }));
        }
      } catch (error) {
        console.warn(
          "[TCE] Erro ao carregar dados da empresa:",
          error?.message,
        );
      }
    };

    const carregarEstudantesCieemg = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(
          `${API_BASE_URL}/tce/estudantes-empresa`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        setTcesPendentesCieemg(
          Array.isArray(response.data) ? response.data : [],
        );
      } catch (error) {
        console.warn(
          "[TCE] Erro ao buscar estudantes encaminhados pelo CIEEMG:",
          error?.response?.status || error?.message,
        );
      }
    };

    const decoded = jwtDecode(localStorage.getItem("token"));

    if (decoded.empresa_id) {
      carregarFiliais();
      carregarUnidadesCieemg();
      carregarEnderecoEmpresa();
      carregarDadosEmpresa();
      carregarEstudantesCieemg();
    }

    const removers = [
      handleInputValidation("#estagiario-empresa", /^[0-9a-zA-Z ]+$/),
      handleInputValidation("#nome-aprendiz", /^[0-9a-zA-Z ]+$/),
      handleInputValidation("#cargo_estagiario", /^[0-9a-zA-Z ]+$/),
      handleInputValidation("#email_estagiario", /^[a-zA-Z0-9@._-]+$/),
      handleInputValidation("#email_estagiarioo", /^[a-zA-Z0-9@._-]+$/),
      handleInputValidation("#formacao_escolar", /^[0-9a-zA-Z ]+$/),
      handleInputValidation(
        "#nome_telefone_estudante_aprovado",
        /^[0-9a-zA-Z ]+$/,
      ),
      handleInputValidation("#local_estagio", /^[0-9a-zA-Z ]+$/),
      handleInputValidation("#escola-faculdade", /^[0-9a-zA-Z ]+$/),
      handleInputValidation("#tce-eight-unidade", /^[0-9a-zA-Z ]+$/),
      handleInputValidation("#tce-eight-curso", /^[0-9a-zA-Z ]+$/),
      handleInputValidation("#orientador-estagio", /^[0-9a-zA-Z ]+$/),
      handleInputValidation("#cargo", /^[0-9a-zA-Z ]+$/),
      handleInputValidation("#nome-estudante-estagiario", /^[0-9a-zA-Z ]+$/),
      handleInputValidation("#tce_nome_termo_compromisso", /^[0-9a-zA-Z ]+$/),
      handleInputValidation("#tce_rg_estagiario", /^[0-9a-zA-Z ]+$/),
      handleInputValidation("#ano_periodo_serie", /^[0-9a-zA-Z ]+$/),
      handleInputValidation("#tce_previsao_formatura", /^[0-9a-zA-Z /]*$/),
      handleInputValidation("#tce-eight-cnpj", /^[0-9a-zA-Z ]+$/),
      handleInputValidation("#inicio-estagio", /^[0-9a-zA-Z /]*$/),
      handleInputValidation("#termino-estagio", /^[0-9a-zA-Z /]*$/),
      handleInputValidation("#nome-menor-idade", /^[0-9a-zA-ZÀ-ÿ ]+$/),
      handleInputValidation("#email_empresa", /^[a-zA-Z0-9@._-]+$/),
      handleInputValidation("#email-menor-idade", /^[a-zA-Z0-9@._-]+$/),
      handleInputValidation("#email", /^[a-zA-Z0-9@._-]+$/),
      handleInputValidation("#email_escola", /^[a-zA-Z0-9@._-]+$/),
    ];
  }, []);

  useEffect(() => {
    const { totalSemanal } = validarHorarios(horario);
    setCargaHorariaSemanal(totalSemanal);
  }, [horario]);

  function calcularIdade(dtNasc) {
    if (!dtNasc) return null;
    const dateStr = String(dtNasc).trim();
    let ano, mes, dia;
    if (/^\d{4}-\d{2}-\d{2}/.test(dateStr)) {
      [ano, mes, dia] = dateStr.substring(0, 10).split("-").map(Number);
    } else if (/^\d{2}\/\d{2}\/\d{4}/.test(dateStr)) {
      [dia, mes, ano] = dateStr.split("/").map(Number);
    } else {
      console.warn("Formato de data não reconhecido:", dateStr);
      return null;
    }
    if (!ano || !mes || !dia || isNaN(ano) || isNaN(mes) || isNaN(dia)) {
      console.warn("Data inválida:", dtNasc);
      return null;
    }
    const nasc = new Date(ano, mes - 1, dia);
    if (isNaN(nasc.getTime())) return null;
    const hoje = new Date();
    let idade = hoje.getFullYear() - nasc.getFullYear();
    const m = hoje.getMonth() - nasc.getMonth();
    if (m < 0 || (m === 0 && hoje.getDate() < nasc.getDate())) idade--;
    console.log(`[calcularIdade] ${dtNasc} → ${idade} anos`);
    return idade;
  }

  function authHeader() {
    const token = localStorage.getItem("token");
    return { Authorization: `Bearer ${token}` };
  }

  const buscarDadosDoEstagiario = async (cpfDigitado) => {
    if (!cpfDigitado) return;
    const cpfLimpo = String(cpfDigitado).replace(/\D/g, "");

    if (cpfLimpo.length !== 11) return;

    try {
      setEstudanteNaoEncontrado(false);
      setCienteCadastro(false);
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${API_BASE_URL}/estudantes/cpf/${cpfLimpo}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      const dadosDoEstudante = response.data;
      console.log(
        "=== DADOS DO ESTUDANTE RETORNADOS PELA API ===",
        dadosDoEstudante,
      );

      if (dadosDoEstudante) {
        const idade = calcularIdade(
          dadosDoEstudante.dt_nascimento || dadosDoEstudante.data_nascimento,
        );
        // O backend pode enviar o nome/CPF do responsável legal em chaves
        // diferentes conforme a versão da rota. Aceitamos todas para
        // compatibilidade (mesma estratégia usada em TCA/index.js).
        const nomeRespBruto =
          dadosDoEstudante.nome_mae ||
          dadosDoEstudante.nomemae ||
          dadosDoEstudante.nome_responsavel_legal ||
          dadosDoEstudante.mae ||
          "";
        const cpfRespBruto =
          dadosDoEstudante.cpf_mae ||
          dadosDoEstudante.cpfmae ||
          dadosDoEstudante.cpf_responsavel_legal ||
          "";
        const cpfRespLimpo = String(cpfRespBruto).replace(/\D/g, "");
        const cpfRespValido =
          cpfRespLimpo.length >= 11 ? cpfRespBruto : "";

        setFormData((prev) => {
          const prevNomeResp = prev.nome_menor_idade || "";
          const prevCpfResp = prev.cpf_menor_idade || "";
          const prevCpfRespValido =
            prevCpfResp.replace(/\D/g, "").length >= 11;
          return {
            ...prev,
            nome_estagiario:
              dadosDoEstudante.nome_completo || dadosDoEstudante.nome || "",
            rg: dadosDoEstudante.rg || "",
            telefone_estagiario:
              dadosDoEstudante.telefone || dadosDoEstudante.telefone1 || "",
            email_estagiario: dadosDoEstudante.email || "",
            estudante_id: dadosDoEstudante.id || null,
            dt_nascimento_estudante:
              dadosDoEstudante.dt_nascimento ||
              dadosDoEstudante.data_nascimento ||
              "",
            // Traz automaticamente Nome e CPF do responsável legal.
            // Só sobrescreve se o backend retornou valor válido; caso
            // contrário, preserva o que o usuário já tenha digitado.
            nome_menor_idade:
              nomeRespBruto || (!prevNomeResp ? "" : prevNomeResp),
            cpf_menor_idade:
              cpfRespValido || (!prevCpfRespValido ? prevCpfResp : ""),
          };
        });
        setIdadeCalculada(idade);
        setEstudanteEncontrado(true);
      }
    } catch (error) {
      console.log("Estudante não encontrado ou erro na busca", error);
      setEstudanteNaoEncontrado(true);
      setEstudanteEncontrado(false);
      setCienteCadastro(false);
      setFormData((prev) => ({
        ...prev,
        nome_estagiario: "",
        telefone_estagiario: "",
        email_estagiario: "",
        rg: "",
        estudante_id: null,
        dt_nascimento_estudante: "",
      }));
      setIdadeCalculada(null);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      const cpfValue = formData.cpf_estudante_estagiario || "";
      const cpfLimpo = String(cpfValue).replace(/\D/g, "");

      if (cpfLimpo.length < 11) {
        setEstudanteNaoEncontrado(false);
        setEstudanteEncontrado(false);
        if (cpfLimpo.length === 0) {
          setFormData((prev) => {
            if (
              prev.nome_estagiario ||
              prev.telefone_estagiario ||
              prev.email_estagiario ||
              prev.rg
            ) {
              return {
                ...prev,
                nome_estagiario: "",
                telefone_estagiario: "",
                email_estagiario: "",
                rg: "",
                estudante_id: null,
                dt_nascimento_estudante: "",
              };
            }
            return prev;
          });
          setIdadeCalculada(null);
        }
        return;
      }

      buscarDadosDoEstagiario(cpfValue);
    }, 1000);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.cpf_estudante_estagiario, user?.acesso_autorizacao]);

  const handleFileChange = (event) => {
    if (event.target.files && event.target.files.length > 0) {
      setSelectedFile(event.target.files[0]);
    }
  };

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    let newValue = value;

    if (name === "origem_candidato") {
      setEstudanteNaoEncontrado(false);
      setCienteCadastro(false);
      setEstudanteEncontrado(false);
      setShowConsentimento(false);
      setCienteSemCadastro(false);
      setIdadeCalculada(null);
      setFormData((prev) => ({ ...prev, dt_nascimento_estudante: "" }));
      setFormData((prev) => ({
        ...prev,
        origem_candidato: value,
        estudante_id: null,
        nome_estagiario: "",
        cpf_estudante_estagiario: "",
        rg: "",
        telefone_estagiario: "",
        email_estagiario: "",
      }));
      return;
    }

    const emojiRegex = /[\p{Extended_Pictographic}]/gu;
    if (
      ![
        "cnpj_cpf",
        "telefones",
        "tce_rg_estagiario",
        "tce-six-cpf",
        "ano_periodo_serie",
        "previsao_formatura",
        "tce-eight-cnpj",
        "valor_bolsa_auxilio",
        "valor_bolsa_transporte",
        "tce-two-row-cpf",
        "nome-menor-idade",
        "email-menor-idade",
        "textarea-observacoes",
      ].includes(name)
    ) {
      newValue = newValue.replace(emojiRegex, "");
    }

    if (type === "checkbox") {
      setFormData((prev) => ({
        ...prev,
        diasSelecionados: checked
          ? [...prev.diasSelecionados, value]
          : prev.diasSelecionados.filter((dia) => dia !== value),
      }));

      return;
    }

    if (name === "data_inicio_contrato") {
      const [day, month, year] = value.split("/");
      newValue = `20${year}-${month}-${day}`;
    } else if (name === "cnpj_cpf") {
      let formattedValue = value.replace(/\D/g, "");
      if (formattedValue.length <= 11) {
        formattedValue = formattedValue.replace(/(\d{3})(\d)/, "$1.$2");
        formattedValue = formattedValue.replace(/(\d{3})(\d)/, "$1.$2");
        formattedValue = formattedValue.replace(/(\d{3})(\d{1,2})/, "$1-$2");
      } else {
        formattedValue = formattedValue.replace(/^(\d{2})(\d)/, "$1.$2");
        formattedValue = formattedValue.replace(
          /^(\d{2})\.(\d{3})(\d)/,
          "$1.$2.$3",
        );
        formattedValue = formattedValue.replace(/\.(\d{3})(\d)/, ".$1/$2");
        formattedValue = formattedValue.replace(/(\d{4})(\d)/, "$1-$2");
      }
      newValue = formattedValue;
    }

    const regexCampos = [
      "empresa",
      "nome",
      "nome_estagiario",
      "cargo_estagiario",
      "formacao_escolar",
      "nome_telefone_estudante_aprovado",
      "local_estagio",
      "escola",
      "unidade",
      "curso",
      "orientador_estagio",
      "cargo",
      "nome_menor_idade",
    ];
    if (regexCampos.includes(name)) {
      newValue = newValue.replace(/[^0-9a-zA-ZÀ-ÿ\s.,&-]/g, "");
    }

    setFormData((prevData) => {
      const updated = { ...prevData, [name]: newValue };

      // Marca que o usuário editou manualmente o campo de local do estágio,
      // para que o auto-preenchimento baseado na filial não sobrescreva.
      if (name === "local_estagio") {
        updated.local_estagio_editado = true;
      }

      if (name === "inicio_estagio" && newValue) {
        try {
          const [yy, mm, dd] = newValue.split("-").map(Number);
          if (yy && mm && dd) {
            const inicioDate = new Date(yy, mm - 1, dd);
            const limite = new Date(inicioDate);
            limite.setFullYear(limite.getFullYear() + 2);
            const limiteStr = [
              limite.getFullYear(),
              String(limite.getMonth() + 1).padStart(2, "0"),
              String(limite.getDate()).padStart(2, "0"),
            ].join("-");

            const terminoAtual = prevData.termino_estagio;
            const terminoJaPersonalizado =
              terminoAtual &&
              terminoAtual !== prevData.termino_estagio_autopreenchido;

            if (!terminoJaPersonalizado) {
              updated.termino_estagio = limiteStr;
              updated.termino_estagio_autopreenchido = limiteStr;
            }
          }
        } catch (_) {
        }
      }

      if (name === "termino_estagio" && newValue) {
        updated.termino_estagio_autopreenchido = null;
      }

      if (hasDuplicateCPF(updated)) {
        setError("⚠️ Você repetiu um CPF em mais de um campo.");
      } else {
        setError("");
      }

      return updated;
    });
  };

  const handleHorarioChange = (tipo, dia, campo, valor) => {
    valor = valor.replace(/\D/g, "");

    setFormData((prev) => {
      if (tipo === "fixo") {
        return {
          ...prev,
          horarioFixo: {
            ...prev.horarioFixo,
            [campo]: valor,
          },
        };
      }

      return {
        ...prev,
        horariosAlternados: {
          ...prev.horariosAlternados,
          [dia]: {
            ...prev.horariosAlternados[dia],
            [campo]: valor,
          },
        },
      };
    });
  };

  const handleArquivoSupervisor = (e) => {
    const file = e.target.files[0];

    if (!file) return;

    const formatos = ["application/pdf", "image/png", "image/jpeg"];

    if (!formatos.includes(file.type)) {
      toastWarning("Somente PDF, PNG e JPG são permitidos.");
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toastWarning("Arquivo deve ter no máximo 10MB.");
      return;
    }

    setArquivoSupervisor(file);
  };

  const removerArquivoSupervisor = () => {
    setArquivoSupervisor(null);

    const input = document.getElementById("arquivo-supervisor");

    if (input) {
      input.value = "";
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const token = localStorage.getItem("token");
    const decoded = jwtDecode(token);

    if (!decoded?.empresa_id) {
      toastError("Empresa não identificada.");
      return;
    }

    if (!podeEditarCampo("empresa") && !podeEditarCampo("estudante")) {
      toastError("Sem permissão para editar.");
      return;
    }

    if (hasDuplicateCPF(formData)) {
      toastError("Há CPFs repetidos no formulário.");
      return;
    }

    if (!formData.origem_candidato) {
      toastWarning(
        "Selecione a origem do candidato (CIEEMG ou Candidato Próprio).",
      );
      return;
    }

    if (formData.origem_candidato === "CIEEMG" && !formData.estudante_id) {
      toastWarning("Selecione um estudante da lista do CIEEMG.");
      return;
    }

    if (formData.origem_candidato === "EMPRESA") {
      const cpfLimpo = (formData.cpf_estudante_estagiario || "").replace(
        /\D/g,
        "",
      );
      if (cpfLimpo.length !== 11) {
        toastWarning("Informe um CPF válido para o candidato.");
        return;
      }
      if (!estudanteEncontrado && !cienteCadastro) {
        toastWarning(
          "É obrigatório confirmar a ciência sobre o cadastro do estudante no CIEEMG.",
        );
        return;
      }
      if (!estudanteEncontrado && !formData.nome_estagiario) {
        toastWarning("Informe o nome do estudante.");
        return;
      }
      if (idadeCalculada === null) {
        toastError("Não foi possível calcular a idade do estudante.");
        return;
      }
    }

    try {
      let linkAssinatura = LINK_DOCUSIGN_FALLBACK;

      try {
        const autorizacao = await getAutorizacao(decoded.empresa_id);
        if (autorizacao?.link) {
          linkAssinatura = autorizacao.link;
        } else if (autorizacao === null) {
          console.warn("Backend sem link — usando fallback DocuSign");
        }
      } catch (e) {
        console.warn(
          "Erro ao buscar autorização — usando fallback DocuSign:",
          e,
        );
      }

      let totalSemanal = 0;
      const validacaoHorario = validarHorarios(horario);
      totalSemanal = validacaoHorario.totalSemanal;
      const horarioErrors = validacaoHorario.errors;

      const dadosEnvio = {
        ...formData,
        local_estagio_editado: undefined,
        idade_calculada: idadeCalculada,
        horario_fixo_ou_alternado: {
          tipo: horario.tipo,
          diasSelecionados: horario.diasSelecionados,
          fixo: horario.fixo,
          alternado: horario.alternado,
          carga_horaria_semanal: totalSemanal,
        },
        local_realizacao_atividades: mostrarFiliais
          ? localAtividade || formData.local_realizacao_atividades || "MATRIZ"
          : "MATRIZ",
        anexos_obrigatorios_ids: buildAnexosObrigatoriosIds({
          isEstudante,
          schema: ANEXOS_SCHEMA_TCE,
        }),
        ...buildAnexosObrigatoriosBodyPatch({
          schema: ANEXOS_SCHEMA_TCE,
          anexosFiles,
        }),
      };

      const converterData = (data) => {
        const [dia, mes, ano] = data.split("/");
        return new Date(ano, mes - 1, dia);
      };

      const inicio = converterData(formData.inicio_estagio);
      if (inicio < dataMinimaInicio) {
        toastWarning(
          `A data de início deve ser a partir de ${dataMinimaInicioFormatada}`,
        );
        return;
      }

      const termino = converterData(formData.termino_estagio);
      const limiteFinal = new Date(inicio);
      limiteFinal.setFullYear(limiteFinal.getFullYear() + 2);
      if (termino > limiteFinal) {
        toastWarning("A vigência do estágio não pode ultrapassar 2 anos.");
        return;
      }

      const bolsa =
        Number(
          formData.valor_bolsa_auxilio.replace(/[^\d,]/g, "").replace(",", "."),
        ) || 0;
      if (bolsa <= 0) {
        toastWarning("O valor da bolsa auxílio deve ser maior que zero.");
        return;
      }

      const transporte =
        Number(
          formData.valor_bolsa_transporte
            .replace(/[^\d,]/g, "")
            .replace(",", "."),
        ) || 0;
      if (transporte <= 0) {
        toastWarning("O valor do auxílio transporte deve ser maior que zero.");
        return;
      }

      if (horario.tipo === "") {
        toastWarning("Selecione o tipo de horário (fixo ou alternado).");
        return;
      }

      if (horarioErrors.length > 0) {
        toastWarning("Há erros no horário: " + horarioErrors.join(" • "));
        return;
      }

      const horasValidas = totalSemanal > 0 && totalSemanal <= 30;
      if (!horasValidas) {
        toastWarning(
          `Carga horária semanal inválida: ${totalSemanal.toFixed(1)}h. Limite: 30h.`,
        );
        return;
      }

      if (possuiIncompatibilidade && !arquivoSupervisor) {
        toastWarning(
          "É obrigatório anexar o documento comprobatório de experiência do supervisor.",
        );
        return;
      }

      if (!bypassingReviewRef.current) {
        setReviewRole("empresa");
        setShowReview(true);
        return;
      }
      bypassingReviewRef.current = false;

      const response = await axios.post(`${API_BASE_URL}/tce`, dadosEnvio, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const tceId = response.data?.id || response.data?.tceId || "?";
      setTceIdState(tceId);

      const filialIdNum = Number(localAtividade);
      const filialPayload = {
        nome_filial: formData.nome_filial,
        cnpj: formData.cpfCnpj,
        telefone_1: formData.telefones,
        telefone_2: formData.telefones2,
        bairro: formData.bairro,
        cidade: formData.municipio,
        uf: formData.uf,
        email_contratos: formData.emailContratos,
        email_financeiro: formData.emailFinanceiro,
        representante_nome: formData.representadaPor,
        representante_cargo: formData.cargoRepresentante,
        representante_email: formData.emailRepresentante,
        supervisor_nome: formData.responsavelSupervisor,
        supervisor_cargo: formData.cargoSupervisor,
        supervisor_email: formData.emailSupervisor,
      };
      if (
        mostrarFiliais &&
        localAtividade &&
        localAtividade !== "MATRIZ" &&
        localAtividade !== "FILIAL" &&
        !isNaN(filialIdNum)
      ) {
        try {
          await axios.put(
            API_ENDPOINTS.filial_update(filialIdNum),
            filialPayload,
            { headers: { Authorization: `Bearer ${token}` } },
          );
          toastSuccess("Dados da filial atualizados.");
        } catch (filialErr) {
          console.warn(
            "Falha ao persistir filial (PUT /filiais/:id):",
            filialErr,
            filialErr?.response?.data,
          );
          const status = filialErr.response?.status;
          const backendMsg =
            filialErr.response?.data?.message ||
            filialErr.response?.data?.erro ||
            filialErr.response?.data?.error ||
            filialErr.message ||
            "Erro ao atualizar dados da filial";
          if (status === 403) {
            toastError(
              "Sem permissão para atualizar a filial. Contate o administrador do sistema.",
            );
          } else if (status === 401) {
            toastError(
              "Sessão expirada. Faça login novamente para atualizar a filial.",
            );
          } else if (status === 404) {
            toastError("Filial não encontrada no servidor.");
          } else if (status >= 500) {
            toastError(`Erro no servidor ao atualizar filial: ${backendMsg}`);
          } else {
            toastError(backendMsg);
          }
        }
      }

      if (arquivoSupervisor) {
        const form = new FormData();
        form.append("foto", arquivoSupervisor);
        form.append("tce_id", response.data.id);
        form.append("tipo_documento", "comprovante_supervisor");

        await axios.post(`${API_BASE_URL}/fotos`, form, {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        });
        console.log(response.data);
      }

      const isEmpresa =
        decoded?.tipo === "empresa" || decoded?.tipo_programa === 0;
      setSuccessModalData({
        isOpen: true,
        tceId: tceId,
        role: isEmpresa ? "empresa" : "estagiario",
      });
    } catch (error) {
      console.error(error);
      const resData = error.response?.data;

      if (error.response?.status === 400 && resData) {
        const { anexosObrigatorios, camposFaltando } =
          parseAnexosObrigatoriosFromError(resData);

        if (anexosObrigatorios.length > 0 || camposFaltando.length > 0) {
          setAnexosObrigatoriosDestacar(
            anexosObrigatorios.map(a => a.id).filter(Boolean),
          );

          const seen = new Set();
          const items = [];
          const addItem = (id, label) => {
            const key = String(id || "").trim();
            if (!key || seen.has(key)) return;
            seen.add(key);
            items.push({ id: key, label: label || key });
          };
          anexosObrigatorios.forEach(a => addItem(a.id, a.label));
          camposFaltando.forEach(c => addItem(c, c));
          setMissingFieldsAlert(items);
          return;
        }
      }

      let errorMsg = "Erro ao salvar o TCE.";

      if (resData?.errors && Array.isArray(resData.errors)) {
        const firstError = resData.errors[0];
        if (typeof firstError === "string") {
          errorMsg = firstError;
        } else if (firstError?.errors && Array.isArray(firstError.errors)) {
          errorMsg = firstError.errors.join(" | ");
        } else if (firstError?.mensagem) {
          errorMsg = firstError.mensagem;
        }
      } else if (resData?.message) {
        errorMsg = resData.message;
      } else if (resData?.error) {
        errorMsg = resData.error;
      }

      toastError(errorMsg);
    }
  };

  const clearFileInput = () => {
    inputRef.current.value = "";
    setSelectedFile(null);
    setProgress(0);
    setUploadStatus("select");
  };

  const handleUpload = async (file) => {
    if (!file) return;
    try {
      setUploadStatus("uploading");
      const formData = new FormData();
      formData.append("file", file);

      // "https://apiautorizacao.cieemg.org.br/upload"

      const token = localStorage.getItem("token");
      const decoded = token ? jwtDecode(token) : null;
      const empresaIdToUse = formData.empresa_id || decoded?.empresa_id;

      if (!empresaIdToUse) {
        console.error("Empresa ID inválido");
        return;
      }

      const response = await axios.post(
        `${API_BASE_URL}/tca/empresa/${empresaIdToUse}/autorizacao`,
        formData,
        {
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total,
            );
            setProgress(percentCompleted);
          },
        },
      );

      console.log("Upload concluído:", response.data);
      setUploadStatus("done");
      clearFileInput();
    } catch (error) {
      console.error("Erro no upload:", error);
    }
  };

  const handleAnexoChange = (key) => async (file) => {
    if (!file) return;

    const allowed = ["application/pdf", "image/png", "image/jpeg"];
    if (!allowed.includes(file.type)) {
      toastWarning("Formato inválido. Use PDF, PNG ou JPG.");
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toastWarning("Arquivo maior que 10MB.");
      return;
    }

    setAnexosFiles((prev) => ({ ...prev, [key]: file }));
    setAnexosStatus((prev) => ({ ...prev, [key]: "uploading" }));
    setAnexosProgress((prev) => ({ ...prev, [key]: 0 }));

    try {
      const token = localStorage.getItem("token");

      const formData = new FormData();
      formData.append("file", file);

      const uploadResp = await axios.post(
        `${API_BASE_URL}/upload`,
        formData,
        {
          headers: { Authorization: `Bearer ${token}` },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total,
            );
            setAnexosProgress((prev) => ({ ...prev, [key]: percentCompleted }));
          },
        }
      );

      const data = uploadResp?.data || {};
      const persistedPath =
        data.path ||
        data.filename ||
        data.file ||
        data.url ||
        data.location ||
        data.caminho ||
        data.arquivo ||
        data.arquivo_path ||
        data?.data?.path ||
        data?.data?.filename ||
        data?.file?.path ||
        data?.file?.filename ||
        null;

      const fallbackPath = file?.name
        ? `uploads/${Date.now()}_${file.name}`
        : null;
      const finalPath = persistedPath || fallbackPath;
      if (finalPath) {
        const originalname =
          data.originalname || data?.file?.originalname || file.name || null;
        setAnexosFiles((prev) => {
          const existing = prev[key];
          if (existing && existing instanceof File) {
            Object.assign(existing, {
              filename: finalPath,
              originalname,
              persisted: true,
              uploadResponse: data,
            });
            return prev;
          }
          return {
            ...prev,
            [key]: {
              filename: finalPath,
              originalname,
              persisted: true,
              uploadResponse: data,
              _file: file,
            },
          };
        });
      }

      setAnexosStatus((prev) => ({ ...prev, [key]: "done" }));
    } catch (error) {
      console.error("Erro no upload:", error);
      setAnexosStatus((prev) => ({ ...prev, [key]: "error" }));
      toastError("Erro ao enviar arquivo. Tente novamente.");
    }
  };

  const isEmpresaDisabled =
    !permissao.podeEditarTudo && !permissao.podeEditarEmpresa;
  const isEstudanteDisabled =
    !permissao.podeEditarTudo && !permissao.podeEditarEstudante;
  const isEmpresaReadOnly =
    permissao?.label === "Empresa" || permissao?.label === "Estudante";
  const isEstudanteReadOnly = isEstudante;

  const carregarDadosEmpresa = async () => {
    try {
      const token = localStorage.getItem("token");

      const response = await axios.get(`${API_BASE_URL}/tce/dados-empresa`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setFormData((prev) => ({
        ...prev,
        empresa: response.data.razaosocial || "",
        telefones: formatarTelefone(response.data.telefone) || "",
        cnpj_cpf: formatarCnpj(response.data.cnpj) || "",
        email_empresa:
          response.data.email ||
          response.data.email_empresa ||
          response.data.email_contato ||
          "",
      }));
    } catch (err) {
      console.error("Erro ao carregar dados da empresa:", err);
    }
  };

  const carregarEstudantesCieemg = async () => {
    try {
      setLoadingEstudantes(true);
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${API_BASE_URL}/tce/estudantes-empresa`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setEstudantesCieemg(response.data || []);
    } catch (err) {
      console.error("Erro ao carregar estudantes do CIEEMG:", err);
    } finally {
      setLoadingEstudantes(false);
    }
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const id = urlParams.get("id");

    if (id) {
      setModoEdicao(true);
      setTceId(id);
      carregarTceExistente(id);
      return;
    }

    try {
      const stored = localStorage.getItem('estudante_tce_id');
      if (stored) {
        const storedId = String(stored).trim();
        if (storedId && /^\d+$/.test(storedId)) {
          setModoEdicao(true);
          setTceId(storedId);
          carregarTceExistente(storedId);
        }
      }
    } catch (_) { /* localStorage indisponível */ }
  }, []);

  const verificarPermissaoTCE = () => {
    const token = localStorage.getItem("token");
    if (!token) return false;
    try {
      const decoded = jwtDecode(token);
      const tipo = decoded.tipo;
      const tipoPrograma = Number(decoded.tipo_programa);
      if (tipo === "usuario_ciee") return true;
      if (tipoPrograma === 0 || tipoPrograma === 1 || tipoPrograma === 2) {
        return true;
      }
      return false;
    } catch (e) {
      return false;
    }
  };

  if (!verificarPermissaoTCE()) {
    return <Navigate to="/dashboard" replace />;
  }

  const carregarTceExistente = async (id) => {
    try {
      const token = localStorage.getItem("token");

      const responseTce = await axios.get(`${API_BASE_URL}/tce/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const dadosTce = { ...responseTce.data };
      [
        "inicio_estagio",
        "termino_estagio",
        "data_nascimento",
        "dt_nascimento_estudante",
        "data_emissao_rg",
      ].forEach((c) => {
        if (dadosTce[c]) {
          dadosTce[c] = toDateInputValue(dadosTce[c]);
        }
      });

      const statusFinal =
        dadosTce.status ||
        dadosTce.situacao ||
        dadosTce.estado ||
        dadosTce.state ||
        dadosTce.etapa ||
        dadosTce.tce_status ||
        null;
      setTceStatus(statusFinal);
      const valorBruto = responseTce.data.local_realizacao_atividades
        ? String(responseTce.data.local_realizacao_atividades)
        : "";
      if (valorBruto) {
        const isNumerico =
          !isNaN(Number(valorBruto)) &&
          valorBruto !== "MATRIZ" &&
          valorBruto !== "FILIAL";
        if (isNumerico) {
          dadosTce.local_realizacao_atividades = "FILIAL";
          setLocalAtividade(valorBruto);
        } else if (valorBruto === "MATRIZ") {
          dadosTce.local_realizacao_atividades = "MATRIZ";
          setLocalAtividade("");
        } else {
          // valorBruto === "FILIAL" sem ID específico
          setLocalAtividade("");
        }
      }
      setFormData({ ...formData, ...dadosTce });

      const horarioSalvo = dadosTce.horario_fixo_ou_alternado;
      if (horarioSalvo && typeof horarioSalvo === "object") {
        setHorario((prev) => ({
          tipo:
            typeof horarioSalvo.tipo === "string"
              ? horarioSalvo.tipo
              : prev.tipo,
          diasSelecionados: Array.isArray(horarioSalvo.diasSelecionados)
            ? horarioSalvo.diasSelecionados
            : prev.diasSelecionados,
          fixo:
            horarioSalvo.fixo && typeof horarioSalvo.fixo === "object"
              ? { ...prev.fixo, ...horarioSalvo.fixo }
              : prev.fixo,
          alternado:
            horarioSalvo.alternado &&
            typeof horarioSalvo.alternado === "object"
              ? { ...prev.alternado, ...horarioSalvo.alternado }
              : prev.alternado,
        }));
        if (
          typeof horarioSalvo.carga_horaria_semanal === "number" &&
          !Number.isNaN(horarioSalvo.carga_horaria_semanal)
        ) {
          setCargaHorariaSemanal(horarioSalvo.carga_horaria_semanal);
        }
      }

      setAnexosFiles((prevFiles) =>
        hydrateAnexosFilesFromPayload({
          schema: ANEXOS_SCHEMA_TCE,
          payload: dadosTce,
          prev: prevFiles,
        }),
      );

      try {

        if (![3, 4].includes(nivel)) {
          setTceHistorico([]);
        } else {
          const responseHist = await axios.get(
            `${API_BASE_URL}/tce/${id}/historico`,
            {
              headers: { Authorization: `Bearer ${token}` },
            },
          );
          const rawHist =
            responseHist.data?.historico ?? responseHist.data ?? [];

          let parsedHist = rawHist;
          if (typeof parsedHist === "string") {
            try {
              parsedHist = JSON.parse(parsedHist);
            } catch (_) {
              parsedHist = [];
            }
          }
          setTceHistorico(Array.isArray(parsedHist) ? parsedHist : []);
        }
      } catch (err) {
        console.warn(
          "Sem permissão para ver histórico (ok se não for SEOPE/SECEX):",
          err,
        );
        setTceHistorico([]);
      }
    } catch (err) {
      console.error("Erro ao carregar TCE:", err);
      const status = err?.response?.status;

      if (status === 403) {
        toastError(
          "Esta autorização não está vinculada ao seu CPF. " +
            "Verifique se você entrou com o usuário correto.",
        );
        navigate("/dashboard", { replace: true });
        return;
      }
      if (status === 404) {
        toastError("Autorização não encontrada.");
        navigate("/dashboard", { replace: true });
        return;
      }

      toastError("Erro ao carregar TCE existente.");
    }
  };

  const diasSemana = [
    { label: "Segunda", key: "segunda" },
    { label: "Terça", key: "terca" },
    { label: "Quarta", key: "quarta" },
    { label: "Quinta", key: "quinta" },
    { label: "Sexta", key: "sexta" },
    { label: "Sábado", key: "sabado" },
    { label: "Domingo", key: "domingo" },
  ];

  const getStatusCor = (status) => {
    const s = String(status || "").toUpperCase();

    if (s === "INÍCIO" || s === "INICIO") return "#005ac2";
    if (s.includes("APROVADO") || s.includes("APROVAR")) return "#16a34a";
    if (
      s.includes("REJEITADO") ||
      s.includes("REPROVADO") ||
      s.includes("RECUSADO") ||
      s.includes("REJEITAR") ||
      s.includes("REPROVAR")
    )
      return "#dc2626";
    if (
      s.includes("PENDENTE") ||
      s.includes("AGUARDANDO") ||
      s.includes("EM_VALIDACAO") ||
      s.includes("CONTRATO_CONFECCIONADO") ||
      s.includes("DISPONIBILIZADO")
    )
      return "#eab308";

    return "#3b82f6";
  };

  if (!authorized) return null;

  function checkChar(e) {
    const char = String.fromCharCode(e.keyCode);

    const pattern = "[a-zA-Z0-9 ]";

    if (char.match(pattern)) {
      return true;
    }
  }

  const onSubmit = () => {};

  const iniciarValidacao = async () => {
    if (!tceId) return;
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API_BASE_URL}/tce/${tceId}/iniciar-validacao`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      toastSuccess("Validação iniciada!");
      carregarTceExistente(tceId);
    } catch (err) {
      toastError(
        err.response?.data?.errors?.[0] || "Erro ao iniciar validação",
      );
    }
  };

  const aprovar = async () => {
    if (!tceId) return;
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API_BASE_URL}/tce/${tceId}/aprovar-validacao`,
        {
          observacao: "Validação aprovada",
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      toastSuccess("TCE aprovado!");
      carregarTceExistente(tceId);
    } catch (err) {
      toastError(err.response?.data?.errors?.[0] || "Erro ao aprovar");
    }
  };

  const recusar = async () => {
    if (!tceId) return;
    const observacao = prompt("Motivo da recusa:");
    if (!observacao) return;
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API_BASE_URL}/tce/${tceId}/reprovar-validacao`,
        {
          destino: "empresa",
          observacao,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      toastWarning("TCE reprovado!");
      carregarTceExistente(tceId);
    } catch (err) {
      toastError(err.response?.data?.errors?.[0] || "Erro ao reprovar");
    }
  };

  const marcarConfeccionado = async () => {
    if (!tceId) return;
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API_BASE_URL}/tce/${tceId}/marcar-confecionado`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      toastSuccess("Contrato marcado como confeccionado!");
      carregarTceExistente(tceId);
    } catch (err) {
      toastError(err.response?.data?.errors?.[0] || "Erro");
    }
  };

  const marcarDisponibilizado = async () => {
    if (!tceId) return;
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API_BASE_URL}/tce/${tceId}/marcar-disponibilizado`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      toastSuccess("Contrato marcado como disponibilizado!");
      carregarTceExistente(tceId);
    } catch (err) {
      toastError(err.response?.data?.errors?.[0] || "Erro");
    }
  };

  const finalizar = async () => {
    if (!tceId) return;
    const ok = await confirm({
      title: "Finalizar processo",
      message: "Confirmar finalização do processo?\nEsta ação é definitiva.",
      confirmText: "Sim, finalizar",
      cancelText: "Cancelar",
      variant: "default",
    });
    if (!ok) return;

    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API_BASE_URL}/tce/${tceId}/finalizar`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      toastSuccess("Processo finalizado com sucesso!");
      carregarTceExistente(tceId);
    } catch (err) {
      toastError(err.response?.data?.errors?.[0] || "Erro");
    }
  };

  const submeterValidacaoEstudante = async () => {
    const tceIdFinal = tceIdState || tceId;
    if (!tceIdFinal) return;

    if (!bypassingReviewRef.current) {
      const anexosFaltando = getAnexosObrigatoriosFaltandoTce();
      if (anexosFaltando.length > 0) {
        setAnexosObrigatoriosDestacar(anexosFaltando.map(d => d.id));
        setMissingFieldsAlert(
          anexosFaltando.map((d) => ({
            id: d.id,
            label: d.label,
          })),
        );
        return;
      }
      setReviewRole("estudante");
      setMissingFieldsAlert([]);
      setShowReview(true);
      return;
    }
    bypassingReviewRef.current = false;

    try {
      const token = localStorage.getItem("token");

      const payloadAtualizacao = {
        ...formData,
        anexos_obrigatorios_ids: buildAnexosObrigatoriosIds({
          isEstudante,
          schema: ANEXOS_SCHEMA_TCE,
        }),

        ...buildAnexosObrigatoriosBodyPatch({
          schema: ANEXOS_SCHEMA_TCE,
          anexosFiles,
        }),
      };
      await axios.put(
        `${API_BASE_URL}/tce/${tceIdFinal}`,
        payloadAtualizacao,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      await axios.post(
        `${API_BASE_URL}/tce/${tceIdFinal}/submeter-validacao-estudante`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      toastSuccess("TCE submetido para validação do CIEEMG!");
      carregarTceExistente(tceIdFinal);
    } catch (err) {
      if (err.response?.status === 400) {
        const { anexosObrigatorios, camposFaltando } =
          parseAnexosObrigatoriosFromError(err.response.data);

        if (anexosObrigatorios.length > 0 || camposFaltando.length > 0) {
          setAnexosObrigatoriosDestacar(
            anexosObrigatorios.map(a => a.id).filter(Boolean),
          );

          const seen = new Set();
          const items = [];
          const addItem = (id, label) => {
            const key = String(id || "").trim();
            if (!key || seen.has(key)) return;
            seen.add(key);
            items.push({ id: key, label: label || key });
          };
          anexosObrigatorios.forEach(a => addItem(a.id, a.label));
          camposFaltando.forEach(c => addItem(c, c));
          setMissingFieldsAlert(items);

          const idsAnexos = anexosObrigatorios.map(a => a.id).filter(Boolean);
          const idsCampos = camposFaltando;
          const msgParts = [];
          if (idsAnexos.length > 0)
            msgParts.push(`Anexos pendentes: ${idsAnexos.join(", ")}`);
          if (idsCampos.length > 0)
            msgParts.push(`Campos pendentes: ${idsCampos.join(", ")}`);
          toastWarning(
            `Preencha os dados obrigatórios para enviar:\n${msgParts.join("\n")}`,
          );
          return;
        }
      }
      toastError(
        err.response?.data?.errors?.[0] ||
          err.response?.data?.message ||
          err.response?.data?.error ||
          "Erro ao submeter validação.",
      );
    }
  };

  const salvarAlteracoesExistente = async () => {
    const tceIdFinal = tceIdState || tceId;
    if (!tceIdFinal) {
      toastWarning("Nenhum TCE carregado para atualizar.");
      return;
    }
    try {
      const token = localStorage.getItem("token");
      const payloadAtualizacao = {
        ...formData,
        idade_calculada: idadeCalculada,
        horario_fixo_ou_alternado: {
          tipo: horario.tipo,
          diasSelecionados: horario.diasSelecionados,
          fixo: horario.fixo,
          alternado: horario.alternado,
          carga_horaria_semanal: cargaHorariaSemanal,
        },
        local_realizacao_atividades: mostrarFiliais
          ? localAtividade || formData.local_realizacao_atividades || "MATRIZ"
          : "MATRIZ",
        anexos_obrigatorios_ids: buildAnexosObrigatoriosIds({
          isEstudante,
          schema: ANEXOS_SCHEMA_TCE,
        }),
        ...buildAnexosObrigatoriosBodyPatch({
          schema: ANEXOS_SCHEMA_TCE,
          anexosFiles,
        }),
      };
      await axios.put(
        `${API_BASE_URL}/tce/${tceIdFinal}`,
        payloadAtualizacao,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      toastSuccess("Alterações salvas.");
      carregarTceExistente(tceIdFinal);
    } catch (err) {
      console.error("Erro ao salvar alterações:", err);
      toastError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          "Erro ao salvar alterações.",
      );
    }
  };

  const getAutorizacao = async (empresaId) => {
    try {
      const token = localStorage.getItem("token");

      const response = await axios.get(
        `${API_BASE_URL}/tce/empresa/${empresaId}/autorizacao`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      return response.data;
    } catch (error) {
      console.error("Erro ao buscar autorização:", error);
      return null;
    }
  };

  const hoje = new Date();

  const dataMinimaInicio = new Date();
  dataMinimaInicio.setDate(hoje.getDate() + 5);
  const dataMinimaInicioFormatada =
    dataMinimaInicio.toLocaleDateString("pt-BR");
  const dataMinimaInicioFormatadaISO = dataMinimaInicio
    .toISOString()
    .split("T")[0];

  const handleSupervisorFileChange = (e) => {
    const file = e.target.files[0];

    if (!file) return;

    const tiposPermitidos = ["application/pdf", "image/png", "image/jpeg"];

    if (!tiposPermitidos.includes(file.type)) {
      toastWarning("Formato inválido. Use PDF, PNG ou JPG.");
      e.target.value = "";
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toastWarning("Arquivo maior que 10MB.");
      e.target.value = "";
      return;
    }

    setArquivoSupervisor(file);
  };

  const visualizarArquivoSupervisor = () => {
    console.log("arquivoSupervisor:", arquivoSupervisor);

    if (!arquivoSupervisor) {
      console.log("Arquivo inexistente");
      return;
    }

    const url = URL.createObjectURL(arquivoSupervisor);

    console.log("URL:", url);

    window.open(url, "_blank");
  };

  return (
    <>
      <form
        className="form-tce form-tce-fade tce-container"
        onSubmit={handleSubmit}
      >
        <h2 className="tce-h2">TCE — Programa Estágio</h2>
        <div className="tce-card">
          <div className="div-dados-empresa-title tce-card-header">
            <label htmlFor="dados-empresa-title">Dados da Empresa</label>
          </div>

          <div className="tce-card-body">
            {permissao.podeEditarEmpresa && (
              <div className="tce-one">
                <div className="div-estagiario-empresa">
                  <label className="all-label" htmlFor="empresa">
                    Empresa
                  </label>
                  <input
                    type="text"
                    name="empresa"
                    className="estagiario-empresa"
                    id="estagiario-empresa"
                    readOnly={isEmpresaReadOnly}
                    onChange={handleChange}
                    value={formData.empresa}
                  />
                </div>
              </div>
            )}

            <div className="tce-one-1">
              <div className="div-tce-cpf-cnpj">
                <label className="all-label" htmlFor="cnpj_cpf">
                  CNPJ ou CPF
                </label>
                <input
                  type="text"
                  name="cnpj_cpf"
                  className="cnpj-cpf"
                  id="cnpj-cpf"
                  maxLength={18}
                  readOnly={isEmpresaReadOnly}
                  onChange={handleChange}
                  value={formData.cnpj_cpf}
                  required
                />
              </div>

              <div className="div-tce-telefones">
                <label className="all-label" htmlFor="telefones">
                  Telefones
                </label>
                <InputMask
                  mask="(99)99999-9999"
                  maskChar=" "
                  type="text"
                  name="telefones"
                  autoComplete="off"
                  className="tce-telefones"
                  id="tce-telefones"
                  readOnly={isEmpresaReadOnly}
                  onChange={handleChange}
                  value={formData.telefones || ""}
                  required
                />
              </div>

              <div className="div-tce-email">
                <label className="all-label" htmlFor="email_empresa">
                  Email
                </label>
                <input
                  type="email"
                  name="email_empresa"
                  className="tce-email"
                  id="email_empresa"
                  maxLength={80}
                  required
                  onChange={handleChange}
                  readOnly={isEmpresaReadOnly}
                  value={formData.email_empresa}
                />
                <span>{errors.email?.message}</span>
              </div>
            </div>

            {permissao?.podeEditarEmpresa === true && (
              <div style={{ marginTop: "1.5rem" }}>
                <div
                  className="div-tca-one"
                  style={{ marginTop: "10px", marginBottom: "20px" }}
                >
                  <div className="div-local-realizacao" id="tce-section-local">
                    <label className="all-label">Unidade</label>
                    <select
                      className="tca-form-control"
                      id="local_realizacao_atividades"
                      name="local_realizacao_atividades"
                      value={formData.local_realizacao_atividades}
                      onChange={(e) => {
                        const val = e.target.value;
                        console.log("[Filial] Unidade mudou para:", val);
                        handleChange(e);
                        // Ao trocar para Matriz, limpa a filial específica
                        // selecionada (se houver). Não auto-selecionamos a
                        // primeira filial ao escolher "Filial" — o usuário
                        // deve escolher manualmente no dropdown abaixo.
                        if (val === "MATRIZ") {
                          setLocalAtividade("");
                          // Se o local_estagio tinha sido auto-preenchido
                          // pela filial anterior e o usuário não editou
                          // manualmente, limpa o campo para que ele informe
                          // o endereço da matriz.
                          setFormData((prev) => {
                            if (prev.local_estagio_editado) return prev;
                            return {
                              ...prev,
                              local_estagio: "",
                            };
                          });
                        }
                      }}
                      required
                    >
                      <option value="MATRIZ">Matriz</option>
                      {mostrarFiliais && <option value="FILIAL">Filial</option>}
                    </select>
                  </div>
                </div>

                {mostrarFiliais &&
                  formData.local_realizacao_atividades === "FILIAL" && (
                    <>
                      <div id="tce-section-filial" />
                      <hr className="tce-hr" style={{ margin: "1.5rem 0" }} />

                      <div style={{ marginBottom: "1rem" }}>
                        <label className="all-label" style={{ fontSize: "1.1rem", fontWeight: "bold" }}>
                          Informações da Filial
                        </label>
                      </div>

                      <div className="tca-col-md-12 tca-mb-3">
                        <label>
                          Selecione a Filial Específica
                          (Auto-preenchimento)
                        </label>
                        <select
                          className="tca-form-control"
                          value={localAtividade}
                          onChange={(e) => {
                            const val = e.target.value;
                            // Importante: NÃO sobrescrever formData.local_realizacao_atividades
                            // com o ID da filial aqui. Esse campo do formData deve
                            // continuar valendo "FILIAL" (string) para que o bloco
                            // Informações da Filial continue visível e o select de
                            // Unidade (Matriz/Filial) não volte para "MATRIZ".
                            // O ID específico fica em `localAtividade` (state local).
                            setLocalAtividade(val);
                          }}
                        >
                          <option value="">Selecione a filial</option>
                          {filiais
                            .filter(
                              (f) =>
                                f.id !== 0 &&
                                f.id !== undefined &&
                                f.id !== null,
                            )
                            .map((f) => (
                              <option key={f.id} value={f.id}>
                                {f.nome_filial ||
                                  f.razao_social ||
                                  f.nome ||
                                  `Filial (ID: ${f.id})`}
                              </option>
                            ))}
                        </select>
                        {filialLoading && (
                          <span
                            style={{
                              color: "#3b82f6",
                              fontSize: "0.85rem",
                              marginTop: "4px",
                              display: "inline-block",
                            }}
                          >
                            Carregando dados da filial...
                          </span>
                        )}
                      </div>

                      <div className="tce-two">
                        <div className="tce-card-body">
                          <div className="tca-row">
                            <div className="tca-col-md-4 tca-mb-3">
                              <label>Nome da Filial</label>
                              <input
                                className="tca-form-control"
                                name="nome_filial"
                                value={formData.nome_filial || ""}
                                onChange={handleChange}
                              />
                            </div>

                            <div className="tca-col-md-4 tca-mb-3">
                              <label>CPF/CNPJ</label>
                              <input
                                type="text"
                                className="tca-form-control"
                                name="cpfCnpj"
                                value={formData.cpfCnpj || ""}
                                onChange={handleChange}
                              />
                            </div>

                            <div className="tca-col-md-4 tca-mb-3">
                              <label>CEP</label>
                              <input
                                type="text"
                                className="tca-form-control"
                                name="cepFilial"
                                value={formData.cepFilial || ""}
                                onChange={handleChange}
                                maxLength={9}
                                placeholder="Apenas números"
                              />
                            </div>

                            <div className="tca-col-md-4 tca-mb-3">
                              <label>Endereço</label>
                              <input
                                type="text"
                                className="tca-form-control"
                                name="enderecoFilial"
                                value={formData.enderecoFilial || ""}
                                onChange={handleChange}
                              />
                            </div>

                            <div className="tca-col-md-4 tca-mb-3">
                              <label>Bairro/Distrito</label>
                              <input
                                type="text"
                                className="tca-form-control"
                                name="bairro"
                                value={formData.bairro || ""}
                                onChange={handleChange}
                              />
                            </div>

                            <div className="tca-col-md-4 tca-mb-3">
                              <label>Municipio</label>
                              <input
                                type="text"
                                className="tca-form-control"
                                name="municipio"
                                value={formData.municipio || ""}
                                onChange={handleChange}
                              />
                            </div>

                            <div className="tca-col-md-4 tca-mb-3">
                              <label>UF</label>
                              <input
                                type="text"
                                className="tca-form-control"
                                name="uf"
                                value={formData.uf || ""}
                                onChange={handleChange}
                              />
                            </div>

                            <div className="tca-col-md-4 tca-mb-3">
                              <label>Telefone 1</label>
                              <InputMask
                                mask="(99)99999-9999"
                                maskChar=" "
                                className="tca-form-control"
                                name="telefones"
                                value={formData.telefones || ""}
                                onChange={handleChange}
                              />
                            </div>

                            <div className="tca-col-md-4 tca-mb-3">
                              <label>Telefone 2</label>
                              <InputMask
                                mask="(99)99999-9999"
                                maskChar=" "
                                className="tca-form-control"
                                name="telefones2"
                                value={formData.telefones2 || ""}
                                onChange={handleChange}
                              />
                            </div>

                            <div className="tca-col-md-4 tca-mb-3">
                              <label>E-mail Contrato</label>
                              <input
                                type="email"
                                className="tca-form-control"
                                name="emailContratos"
                                value={formData.emailContratos || ""}
                                onChange={handleChange}
                              />
                            </div>

                            <div className="tca-col-md-4 tca-mb-3">
                              <label>E-mail Financeiro</label>
                              <input
                                type="email"
                                className="tca-form-control"
                                name="emailFinanceiro"
                                value={formData.emailFinanceiro || ""}
                                onChange={handleChange}
                              />
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="tce-card" style={{ marginTop: "15px" }}>
                        <div className="div-dados-empresa-title tce-card-header">
                          <label>Representante da Filial</label>
                        </div>
                        <div className="tce-card-body">
                          <div className="tca-row">
                            <div className="tca-col-md-4 tca-mb-3">
                              <label>Representante Por</label>
                              <input
                                type="text"
                                className="tca-form-control"
                                name="representadaPor"
                                value={formData.representadaPor || ""}
                                onChange={handleChange}
                              />
                            </div>

                            <div className="tca-col-md-4 tca-mb-3">
                              <label>Cargo</label>
                              <input
                                type="text"
                                className="tca-form-control"
                                name="cargoRepresentante"
                                value={formData.cargoRepresentante || ""}
                                onChange={handleChange}
                              />
                            </div>

                            <div className="tca-col-md-4 tca-mb-3">
                              <label>E-mail</label>
                              <input
                                type="email"
                                className="tca-form-control"
                                name="emailRepresentante"
                                value={formData.emailRepresentante || ""}
                                onChange={handleChange}
                              />
                            </div>

                            <div className="tca-col-md-4 tca-mb-3">
                              <label>Responsável/Supervisor do Estágio</label>
                              <input
                                type="text"
                                className="tca-form-control"
                                name="responsavelSupervisor"
                                value={formData.responsavelSupervisor || ""}
                                onChange={handleChange}
                              />
                            </div>

                            <div className="tca-col-md-4 tca-mb-3">
                              <label>Cargo</label>
                              <input
                                type="text"
                                className="tca-form-control"
                                name="cargoSupervisor"
                                value={formData.cargoSupervisor || ""}
                                onChange={handleChange}
                              />
                            </div>

                            <div className="tca-col-md-4 tca-mb-3">
                              <label>E-mail</label>
                              <input
                                type="email"
                                className="tca-form-control"
                                name="emailSupervisor"
                                value={formData.emailSupervisor || ""}
                                onChange={handleChange}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
              </div>
            )}
          </div>
        </div>

        <div className="tce-card">
          <div className="div-supervisor-estagiario-title tce-card-header">
            <label htmlFor="supervisor-estagiario-title">
              Supervisor do Estagiário
            </label>
          </div>

          <div className="tce-card-body">
            <div className="tce-two">
              <div className="tce-two-row">
                <div className="div-tce-nome-estagiario">
                  <label className="all-label" htmlFor="nome">
                    Nome
                  </label>
                  <input
                    type="text"
                    name="nome"
                    className="nome-aprendiz"
                    id="nome-aprendiz"
                    maxLength={200}
                    disabled={isEmpresaDisabled}
                    onChange={handleChange}
                    value={formData.nome}
                    required
                  />
                </div>

                <div className="div-cargo-estagiario">
                  <label className="all-label" htmlFor="cargo_estagiario">
                    Cargo
                  </label>
                  <input
                    type="text"
                    name="cargo_estagiario"
                    className="nome-aprendiz"
                    id="cargo_estagiario"
                    disabled={isEmpresaDisabled}
                    maxLength={100}
                    onChange={handleChange}
                    value={formData.cargo_estagiario}
                  />
                </div>

                <div className="div-n-cpf-estagiario">
                  <label className="all-label" htmlFor="cpf_estagiario">
                    N° do CPF
                  </label>
                  <InputMask
                    mask="999.999.999-99"
                    maskChar=" "
                    type="text"
                    name="cpf_estagiario"
                    disabled={isEmpresaDisabled}
                    className="tce-two-row-cpf"
                    id="tce-two-row-cpf"
                    onChange={handleChange}
                    value={formData.cpf_estagiario || ""}
                    required
                  />
                </div>
              </div>

              <div className="tce-two-row">
                <div className="div-tce-formacao-escolar">
                  <label className="all-label" htmlFor="formacao_escolar">
                    Formação Escolar
                  </label>
                  <input
                    type="text"
                    name="formacao_escolar"
                    className="nome-aprendiz"
                    id="formacao_escolar"
                    onChange={handleChange}
                    value={formData.formacao_escolar}
                    readOnly={isEstudanteReadOnly}
                    disabled={isEstudanteReadOnly}
                    style={
                      isEstudanteReadOnly
                        ? { backgroundColor: "#e2e8f0" }
                        : undefined
                    }
                    title={
                      isEstudanteReadOnly
                        ? "Campo de responsabilidade da empresa."
                        : undefined
                    }
                  />
                </div>

                <div className="div-tce-email">
                  <label className="all-label" htmlFor="email-supervisor">
                    Email
                  </label>
                  <input
                    type="email"
                    name="email_supervisor"
                    className="tce-email-1"
                    maxLength={100}
                    id="email_supervisor"
                    onChange={handleChange}
                    value={formData.email_supervisor}
                    readOnly={isEstudanteReadOnly}
                    disabled={isEstudanteReadOnly}
                    style={
                      isEstudanteReadOnly
                        ? { backgroundColor: "#e2e8f0" }
                        : undefined
                    }
                    title={
                      isEstudanteReadOnly
                        ? "Campo de responsabilidade da empresa."
                        : undefined
                    }
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* === Quem irá assinar o Termo de Compromisso? (separado do Supervisor) === */}
        <div className="tce-card">
          <div className="div-quem-assina-termo-title tce-card-header">
            <label htmlFor="quem-assina-termo-title">
              Quem irá assinar o Termo de Compromisso? (contrato do estagiário)
            </label>
          </div>

          <div className="tce-card-body">
            <div className="tce-two-row">
              <div className="div-nome-aprendiz">
                <label className="all-label" htmlFor="nome">
                  Nome
                </label>
                <input
                  type="text"
                  name="nome_termo_compromisso"
                  className="nome-aprendiz"
                  id="tce_nome_termo_compromisso"
                  maxLength={100}
                  onChange={handleChange}
                  value={formData.nome_termo_compromisso}
                  readOnly={isEstudanteReadOnly}
                  disabled={isEstudanteReadOnly}
                  style={
                    isEstudanteReadOnly
                      ? { backgroundColor: "#e2e8f0" }
                      : undefined
                  }
                  title={
                    isEstudanteReadOnly
                      ? "Campo de responsabilidade da empresa."
                      : undefined
                  }
                />
              </div>

              <div className="div-tce-email">
                <label className="all-label" htmlFor="email">
                  Email
                </label>
                <input
                  type="email"
                  name="email"
                  className="tce-email-2"
                  id="email"
                  {...register("email")}
                  onChange={handleChange}
                  value={formData.email}
                  readOnly={isEstudanteReadOnly}
                  disabled={isEstudanteReadOnly}
                  style={
                    isEstudanteReadOnly
                      ? { backgroundColor: "#e2e8f0" }
                      : undefined
                  }
                  title={
                    isEstudanteReadOnly
                      ? "Campo de responsabilidade da empresa."
                      : undefined
                  }
                />
              </div>
            </div>

            <div className="tce-two-row">
              <div className="div-nome-aprendiz">
                <label
                  className="all-label"
                  htmlFor="nome_telefone_estudante_aprovado"
                >
                  Nome e Telefone do Estudante aprovado:
                </label>
                <input
                  type="text"
                  name="nome_telefone_estudante_aprovado"
                  className="nome-aprendiz"
                  id="nome_telefone_estudante_aprovado"
                  onChange={handleChange}
                  value={formData.nome_telefone_estudante_aprovado}
                  readOnly={isEstudanteReadOnly}
                  disabled={isEstudanteReadOnly}
                  style={
                    isEstudanteReadOnly
                      ? { backgroundColor: "#e2e8f0" }
                      : undefined
                  }
                  title={
                    isEstudanteReadOnly
                      ? "Campo de responsabilidade da empresa."
                      : undefined
                  }
                />
              </div>
            </div>
          </div>
        </div>

        <div className="tce-card">
          <div
            className="div-dados-estudante-estagiario-title tce-card-header"
            style={{ position: "relative" }}
          >
            <label htmlFor="dados-estudante-estagiario-title">
              Dados do(a) Estudante/Estagiário
            </label>
            {idadeCalculada !== null && (
              <span
                style={{
                  position: "absolute",
                  right: "16px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  fontSize: "0.80rem",
                  padding: "4px 10px",
                  borderRadius: "12px",
                  backgroundColor: idadeCalculada >= 18 ? "#dcfce7" : "#fef08a",
                  color: idadeCalculada >= 18 ? "#166534" : "#854d0e",
                  fontWeight: "700",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  boxShadow: "0 1px 2px rgba(0,0,0,0.15)",
                }}
              >
                {idadeCalculada >= 18 ? "Maior de Idade" : "Menor de Idade"}
              </span>
            )}
          </div>

          <div className="tce-card-body">
            <div
              className="div-origem-candidato"
              style={{ marginBottom: "1.5rem" }}
            >
              <label className="all-label" htmlFor="origem_candidato">
                Origem do Candidato *
              </label>
              <select
                id="origem_candidato"
                name="origem_candidato"
                className="origem-candidato"
                value={formData.origem_candidato || ""}
                onChange={handleChange}
                required
                disabled={isEstudanteReadOnly}
              >
                <option value="">Selecione</option>
                <option value="CIEEMG">Encaminhado pelo CIEEMG</option>
                <option value="EMPRESA">Candidato Próprio da Empresa</option>
              </select>
            </div>

            {estudanteNaoEncontrado && (
              <div
                style={{
                  backgroundColor: "#fef2f2",
                  border: "1px solid #f87171",
                  borderLeft: "4px solid #ef4444",
                  boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                  color: "#991b1b",
                  padding: "1rem 1.25rem",
                  borderRadius: "8px",
                  marginBottom: "1.5rem",
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "12px",
                  lineHeight: "1.5",
                }}
              >
                <AlertCircle
                  size={22}
                  style={{ flexShrink: 0, marginTop: "2px", color: "#ef4444" }}
                />
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "4px",
                  }}
                >
                  <span style={{ fontWeight: "700", fontSize: "0.95rem" }}>
                    Estagiário(a) não encontrado(a)
                  </span>
                  <span style={{ fontSize: "0.9rem", color: "#7f1d1d" }}>
                    O contrato não poderá ser validado enquanto o cadastro do(a)
                    estagiário(a) não for realizado no sistema.
                  </span>
                </div>
              </div>
            )}

            {formData.origem_candidato === "CIEEMG" ? (
              <>
                <div
                  style={{
                    backgroundColor: "#475569",
                    color: "#fbbf24",
                    padding: "1rem 1.25rem",
                    borderRadius: "8px",
                    marginBottom: "1.5rem",
                    display: "flex",
                    flexDirection: "column",
                    gap: "4px",
                  }}
                >
                  <span>
                    Caso não possua cadastro em nosso site, gentileza realizar
                    em:
                  </span>
                  <a
                    href="https://www.cieemg.org.br"
                    style={{
                      color: "#fbbf24",
                      fontWeight: "bold",
                      textDecoration: "underline",
                    }}
                    target="_blank"
                    rel="noreferrer"
                  >
                    www.cieemg.org.br
                  </a>
                </div>

                <div className="tca-mb-3" style={{ marginBottom: "1.5rem" }}>
                  <label className="all-label">
                    Nome do Estudante/Estagiário *
                  </label>
                  <select
                    className="tca-form-control"
                    name="estudante_id"
                    value={formData.estudante_id || ""}
                    disabled={isEstudanteReadOnly}
                    onChange={(e) => {
                      const selectedId = e.target.value;
                      const estudante = tcesPendentesCieemg.find(
                        (est) => String(est.id) === String(selectedId),
                      );
                      if (estudante) {
                        // Aceita o nome/CPF do responsável legal em
                        // qualquer chave que o backend possa retornar.
                        const nomeRespBruto =
                          estudante.nome_mae ||
                          estudante.nomemae ||
                          estudante.nome_responsavel_legal ||
                          estudante.mae ||
                          "";
                        const cpfRespBruto =
                          estudante.cpf_mae ||
                          estudante.cpfmae ||
                          estudante.cpf_responsavel_legal ||
                          "";
                        const cpfRespLimpo = String(cpfRespBruto).replace(
                          /\D/g,
                          "",
                        );
                        const cpfRespValido =
                          cpfRespLimpo.length >= 11 ? cpfRespBruto : "";

                        setFormData((prev) => {
                          const prevNomeResp = prev.nome_menor_idade || "";
                          const prevCpfResp = prev.cpf_menor_idade || "";
                          const prevCpfRespValido =
                            prevCpfResp.replace(/\D/g, "").length >= 11;
                          return {
                            ...prev,
                            estudante_id: selectedId,
                            nome_estagiario:
                              estudante.nome || estudante.nome_completo || "",
                            cpf_estudante_estagiario: estudante.cpf || "",
                            telefone_estagiario:
                              estudante.telefone || estudante.telefone1 || "",
                            email_estagiario: estudante.email || "",
                            rg: estudante.rg || "",
                            dt_nascimento_estudante:
                              estudante.dt_nascimento ||
                              estudante.data_nascimento ||
                              "",
                            nome_menor_idade:
                              nomeRespBruto ||
                              (!prevNomeResp ? "" : prevNomeResp),
                            cpf_menor_idade:
                              cpfRespValido ||
                              (!prevCpfRespValido ? prevCpfResp : ""),
                          };
                        });
                        setIdadeCalculada(
                          calcularIdade(
                            estudante.dt_nascimento ||
                              estudante.data_nascimento,
                          ),
                        );
                      } else {
                        handleChange(e);
                      }
                    }}
                    required
                  >
                    <option value="">Selecione um estudante</option>
                    {tcesPendentesCieemg.map((est) => (
                      <option key={est.id} value={est.id}>
                        {est.nome ||
                          est.nome_completo ||
                          `Estudante (ID: ${est.id})`}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="tca-row">
                  <div className="tca-col-md-3 tca-mb-3">
                    <label className="all-label">CPF</label>
                    <input
                      type="text"
                      className="tca-form-control"
                      value={formData.cpf_estudante_estagiario || ""}
                      readOnly
                      disabled
                      style={{ backgroundColor: "#e2e8f0" }}
                    />
                  </div>
                  <div className="tca-col-md-3 tca-mb-3">
                    <label className="all-label">RG</label>
                    <input
                      type="text"
                      className="tca-form-control"
                      value={formData.rg || ""}
                      readOnly
                      disabled
                      style={{ backgroundColor: "#e2e8f0" }}
                    />
                  </div>
                  <div className="tca-col-md-3 tca-mb-3">
                    <label className="all-label">Telefone</label>
                    <input
                      type="text"
                      className="tca-form-control"
                      value={formData.telefone_estagiario || ""}
                      readOnly
                      disabled
                      style={{ backgroundColor: "#e2e8f0" }}
                    />
                  </div>
                  <div className="tca-col-md-3 tca-mb-3">
                    <label className="all-label">Email</label>
                    <input
                      type="text"
                      className="tca-form-control"
                      value={formData.email_estagiario || ""}
                      readOnly
                      disabled
                      style={{ backgroundColor: "#e2e8f0" }}
                    />
                  </div>
                </div>
              </>
            ) : formData.origem_candidato === "EMPRESA" ? (
              <>
                <div className="tca-row">
                  <div className="tca-col-md-6 tca-mb-3">
                    <label className="all-label">CPF do Estagiário(a):</label>
                    <InputMask
                      mask="999.999.999-99"
                      maskChar=" "
                      type="text"
                      className="tca-form-control"
                      name="cpf_estudante_estagiario"
                      onChange={handleChange}
                      value={formData.cpf_estudante_estagiario || ""}
                      required
                      readOnly={isEstudanteReadOnly}
                      disabled={isEstudanteReadOnly}
                      style={
                        isEstudanteReadOnly
                          ? { backgroundColor: "#e2e8f0" }
                          : undefined
                      }
                    />
                  </div>

                  <div className="tca-col-md-6 tca-mb-3">
                    <label className="all-label">Nome Completo:</label>
                    <input
                      type="text"
                      className="tca-form-control"
                      name="nome_estagiario"
                      maxLength={200}
                      onChange={handleChange}
                      value={formData.nome_estagiario || ""}
                      required
                      readOnly={isEstudanteReadOnly}
                      disabled={isEstudanteReadOnly}
                      style={
                        isEstudanteReadOnly
                          ? { backgroundColor: "#e2e8f0" }
                          : undefined
                      }
                    />
                  </div>

                  <div className="tca-col-md-6 tca-mb-3">
                    <label className="all-label">
                      Telefone/Informações adicionais de contato:
                    </label>
                    <InputMask
                      mask="(99)99999-9999"
                      maskChar=" "
                      type="text"
                      className="tca-form-control"
                      name="telefone_estagiario"
                      autoComplete="off"
                      onChange={handleChange}
                      value={formData.telefone_estagiario || ""}
                      readOnly={isEstudanteReadOnly}
                      disabled={isEstudanteReadOnly}
                      style={
                        isEstudanteReadOnly
                          ? { backgroundColor: "#e2e8f0" }
                          : undefined
                      }
                    />
                  </div>

                  <div className="tca-col-md-6 tca-mb-3">
                    <label className="all-label">E-mail:</label>
                    <input
                      type="email"
                      className="tca-form-control"
                      name="email_estagiario"
                      maxLength={80}
                      onChange={handleChange}
                      value={formData.email_estagiario || ""}
                      readOnly={isEstudanteReadOnly}
                      disabled={isEstudanteReadOnly}
                      style={
                        isEstudanteReadOnly
                          ? { backgroundColor: "#e2e8f0" }
                          : undefined
                      }
                    />
                  </div>
                </div>
              </>
            ) : null}

            {estudanteNaoEncontrado && (
              <div
                style={{
                  backgroundColor: "#ffffff",
                  border: "1px solid #e2e8f0",
                  borderRadius: "12px",
                  padding: "1.25rem 1.5rem",
                  marginBottom: "1.5rem",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "24px",
                  boxShadow:
                    "0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06)",
                  flexWrap: "wrap",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "6px",
                    flex: "1 1 300px",
                  }}
                >
                  <span
                    style={{
                      fontWeight: "700",
                      color: "#1e293b",
                      fontSize: "0.95rem",
                    }}
                  >
                    Precisa de ajuda com o cadastro?
                  </span>
                  <span
                    style={{
                      fontSize: "0.85rem",
                      color: "#475569",
                      lineHeight: "1.5",
                    }}
                  >
                    Em caso de dúvida entre em contato com nossa empresa
                    através.
                    <br />
                    Telefone/Whatsapp: <strong>(31) 3428100</strong> ; E-mail:{" "}
                    <a
                      href="mailto:atendimento@cieemg.org.br"
                      style={{
                        color: "#185fa5",
                        textDecoration: "none",
                        fontWeight: "600",
                      }}
                    >
                      atendimento@cieemg.org.br
                    </a>
                  </span>
                </div>

                <div
                  style={{
                    display: "flex",
                    gap: "12px",
                    alignItems: "center",
                    flexWrap: "wrap",
                  }}
                >
                  <button
                    type="button"
                    onClick={() => setCienteCadastro(true)}
                    disabled={cienteCadastro}
                    style={{
                      backgroundColor: cienteCadastro ? "#dcfce7" : "#f1f5f9",
                      color: cienteCadastro ? "#166534" : "#475569",
                      border: cienteCadastro
                        ? "1px solid #bbf7d0"
                        : "1px solid #cbd5e1",
                      borderRadius: "8px",
                      padding: "0.75rem 1.25rem",
                      fontWeight: "600",
                      fontSize: "0.95rem",
                      cursor: cienteCadastro ? "default" : "pointer",
                      transition: "all 0.2s ease",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    {cienteCadastro ? (
                      <>
                        <Check size={18} />
                        Ciente
                      </>
                    ) : (
                      "Estou Ciente da Situação"
                    )}
                  </button>

                  <button
                    type="button"
                    disabled={!cienteCadastro}
                    onClick={() =>
                      window.open(
                        "https://appcadastro.cieemg.org.br/",
                        "_blank",
                      )
                    }
                    style={{
                      backgroundColor: cienteCadastro ? "#185fa5" : "#94a3b8",
                      color: "#ffffff",
                      border: "none",
                      borderRadius: "8px",
                      padding: "0.75rem 1.75rem",
                      fontWeight: "600",
                      fontSize: "0.95rem",
                      cursor: cienteCadastro ? "pointer" : "not-allowed",
                      boxShadow: cienteCadastro
                        ? "0 4px 6px rgba(24, 95, 165, 0.2)"
                        : "none",
                      transition: "all 0.2s ease",
                      whiteSpace: "nowrap",
                      flexShrink: 0,
                      opacity: cienteCadastro ? 1 : 0.7,
                    }}
                    onMouseOver={(e) => {
                      if (!cienteCadastro) return;
                      e.target.style.backgroundColor = "#10477e";
                      e.target.style.transform = "translateY(-2px)";
                      e.target.style.boxShadow =
                        "0 6px 8px rgba(24, 95, 165, 0.3)";
                    }}
                    onMouseOut={(e) => {
                      if (!cienteCadastro) return;
                      e.target.style.backgroundColor = "#185fa5";
                      e.target.style.transform = "translateY(0)";
                      e.target.style.boxShadow =
                        "0 4px 6px rgba(24, 95, 165, 0.2)";
                    }}
                  >
                    Fazer Cadastro do Estagiário
                  </button>
                </div>
              </div>
            )}
            {/* === Responsável Legal (condicional para menor de idade) === */}
            {idadeCalculada !== null && idadeCalculada < 18 && (
              <div
                className="tce-card"
                style={{
                  marginTop: "1rem",
                  marginBottom: "1rem",
                  border: "1px solid #fde68a",
                  backgroundColor: "#fffbeb",
                }}
              >
                <div className="tce-card-header">
                  <label>Responsável Legal do Estudante/Estagiário</label>
                </div>
                <div className="tce-card-body">
                  <div className="tce-two-row">
                    <div className="div-nome-aprendiz">
                      <label className="all-label" htmlFor="nome_menor_idade">
                        Nome{isEstudante ? ":" : " (opcional):"}
                      </label>
                      <input
                        type="text"
                        name="nome_menor_idade"
                        className="nome-aprendiz"
                        id="nome_menor_idade"
                        maxLength={100}
                        onChange={handleChange}
                        value={formData.nome_menor_idade}
                        required={isEstudante}
                      />
                    </div>

                    <div className="div-tce-email">
                      <label className="all-label" htmlFor="email_menor_idade">
                        Email{isEstudante ? ":" : " (opcional):"}
                      </label>
                      <input
                        type="email"
                        name="email_menor_idade"
                        className="nome-aprendiz"
                        id="email_menor_idade"
                        maxLength={100}
                        onChange={handleChange}
                        value={formData.email_menor_idade}
                        required={isEstudante}
                      />
                    </div>
                  </div>

                  <div className="tce-two-row">
                    <div className="div-tce-telefone-estagiario">
                      <label
                        className="all-label"
                        htmlFor="telefone_menor_idade"
                      >
                        Telefone{isEstudante ? ":" : " (opcional):"}
                      </label>
                      <InputMask
                        mask="(99)99999-9999"
                        maskChar=" "
                        type="text"
                        name="telefone_menor_idade"
                        autoComplete="off"
                        className="tce-telefones"
                        id="telefone_menor_idade"
                        onChange={handleChange}
                        value={formData.telefone_menor_idade || ""}
                        required={isEstudante}
                      />
                    </div>

                    <div className="div-tce-cpf-estagiario">
                      <label className="all-label" htmlFor="cpf_menor_idade">
                        CPF{isEstudante ? ":" : " (opcional):"}
                      </label>
                      <InputMask
                        mask="999.999.999-99"
                        maskChar=" "
                        type="text"
                        name="cpf_menor_idade"
                        className="tce-six-cpf"
                        id="cpf_menor_idade"
                        onChange={handleChange}
                        value={formData.cpf_menor_idade || ""}
                        required={isEstudante}
                      />
                    </div>
                  </div>

                  <div
                    className="alerta-responsavel-legal"
                    style={{ marginTop: "0.5rem" }}
                  >
                    ⚠️ O contrato do estagiário menor de idade deverá ser
                    assinado também pelo responsável legal.
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="tce-card">
          <div className="div-dados-estagiario-title tce-card-header">
            <label htmlFor="dados-estagiario-title">Dados do Estágio</label>
          </div>

          <div className="tce-card-body ">
            <div className="tce-four">
              <div className="tce-four-1">
                <div className="div-tce-four-1-inicio">
                  <label className="all-label" htmlFor="inicio_estagio">
                    Início
                  </label>
                  <input
                    type="date"
                    name="inicio_estagio"
                    className="inicio-estagio"
                    id="inicio-estagio"
                    disabled={isEmpresaDisabled}
                    maxLength={10}
                    onChange={handleChange}
                    value={formData.inicio_estagio}
                  />
                </div>

                <div className="div-tce-four-1-termino">
                  <label className="all-label" htmlFor="termino_estagio">
                    Término
                  </label>
                  <input
                    type="date"
                    name="termino_estagio"
                    className="termino-estagio"
                    id="termino-estagio"
                    disabled={isEmpresaDisabled}
                    maxLength={10}
                    min={formData.inicio_estagio || undefined}
                    max={calcularMaxTerminoEstagio(formData.inicio_estagio)}
                    onChange={handleChange}
                    value={formData.termino_estagio}
                  />
                  {formData.inicio_estagio && (
                    <small
                      style={{
                        display: "block",
                        marginTop: "4px",
                        color: "#6c757d",
                      }}
                    >
                      Limite máximo (2 anos a partir do início):{" "}
                      <strong>
                        {formatarDataBR(
                          calcularMaxTerminoEstagio(formData.inicio_estagio),
                        )}
                      </strong>
                    </small>
                  )}
                </div>

                <div className="div-tce-four-1-valor-bolsa-auxilio">
                  <label className="all-label" htmlFor="valor_bolsa_auxilio">
                    Valor da Bolsa Auxílio
                  </label>
                  <NumericFormat
                    name="valor_bolsa_auxilio"
                    className="nome-aprendiz"
                    id="valor_bolsa_auxilio"
                    disabled={isEmpresaDisabled}
                    isAllowed={isPositiveNumber}
                    thousandSeparator={true}
                    value={formData.valor_bolsa_auxilio}
                    decimalScale={2}
                    fixedDecimalScale={true}
                    prefix="R$ "
                    onValueChange={(values) => {
                      setFormData((prevData) => ({
                        ...prevData,
                        valor_bolsa_auxilio: values.formattedValue,
                      }));
                    }}
                  />
                </div>

                <div className="div-tce-four-1-valor-bolsa-transporte">
                  <label className="all-label" htmlFor="valor_bolsa_transporte">
                    Valor do Auxílio Transporte
                  </label>
                  <NumericFormat
                    name="valor_bolsa_transporte"
                    className="nome-aprendiz"
                    id="valor_bolsa_transporte"
                    disabled={isEmpresaDisabled}
                    thousandSeparator={true}
                    decimalScale={2}
                    fixedDecimalScale={true}
                    prefix="R$"
                    value={formData.valor_bolsa_transporte}
                    onValueChange={(values) => {
                      setFormData((prevData) => ({
                        ...prevData,
                        valor_bolsa_transporte: values.formattedValue,
                      }));
                    }}
                  />
                </div>
              </div>

              <div className="tce-four-2">
                <div className="div-tce-four-label">
                  <label className="tce-four-label" htmlFor="tce-four-label">
                    As datas de vigência poderão ser ajustadas pela equipe do
                    CIEEMG de acordo com as regras e exigências da instituição
                    de ensino.
                  </label>
                </div>

                <div className="dados-estagio-valor-bolsa-transporte">
                  <label className="all-label" htmlFor="local_estagio">
                    Local da realização do estágio
                  </label>
                  <input
                    type="text"
                    name="local_estagio"
                    className="nome-aprendiz"
                    id="local_estagio"
                    maxLength={100}
                    onChange={handleChange}
                    value={formData.local_estagio}
                    disabled={isEmpresaDisabled}
                    title="Informe o endereço onde será a prática do estagiário"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="tce-card">
          <div className="div-dias-horarios-title tce-card-header">
            <label htmlFor="dias-horarios-title">
              Dias e Horários da Realização do Estágio
            </label>
          </div>

          <div className="tce-card-body">
            <div className="tce-five">
              <div className="tce-five-1">
                <div className="div-tipo-contrato">
                  <label className="all-label" htmlFor="tipo_contrato">
                    Tipo de Contrato
                  </label>
                  <select
                    name="tipo_contrato"
                    className="tipo-contrato"
                    id="tipo-contrato"
                    onChange={handleChange}
                    required
                    value={formData.tipo_contrato || ""}
                    disabled={isEstudanteReadOnly}
                  >
                    <option value="" disabled>
                      Selecione
                    </option>
                    <option value="PRESENCIAL">Estágio Presencial</option>
                    <option value="HIBRIDO">Estágio Híbrido</option>
                    <option value="HOMEOFFICE">Estágio Home Office</option>
                  </select>
                </div>

                <div className="div-horario-fixo-alternado">
                  <label
                    className="all-label"
                    htmlFor="horario_fixo_ou_alternado"
                  >
                    Horário fixo ou alternado
                  </label>
                  <select
                    name="horario_tipo"
                    className="tipo-contrato"
                    id="horario_tipo"
                    value={horario.tipo}
                    onChange={(e) =>
                      setHorario((prev) => ({ ...prev, tipo: e.target.value }))
                    }
                    required
                    disabled={isEstudanteReadOnly}
                  >
                    <option value="" disabled>
                      Selecione
                    </option>
                    <option value="fixo">Horário Fixo</option>
                    <option value="alternado">Horário Alternado</option>
                  </select>
                </div>
              </div>

              {horario.tipo === "fixo" && (
                <div className="horario-card tce-five-2">
                  <h4>
                    Horário Fixo{" "}
                    <small>(mesmo horário todos os dias selecionados)</small>
                  </h4>
                  <div className="horario-grid">
                    <div className="dias-semana">
                      <label>Dias da semana de atuação *</label>
                      {[
                        "Segunda",
                        "Terça",
                        "Quarta",
                        "Quinta",
                        "Sexta",
                        "Sábado",
                        "Domingo",
                      ].map((dia) => {
                        const key = dia
                          .toLowerCase()
                          .replace("ç", "c")
                          .replace("á", "a")
                          .replace("ã", "a");
                        return (
                          <label key={dia} className="checkbox-dia">
                            <input
                              type="checkbox"
                              checked={horario.diasSelecionados.includes(dia)}
                              disabled={isEstudanteReadOnly}
                              onChange={(e) => {
                                const novos = e.target.checked
                                  ? [...horario.diasSelecionados, dia]
                                  : horario.diasSelecionados.filter(
                                      (d) => d !== dia,
                                    );
                                setHorario((prev) => ({
                                  ...prev,
                                  diasSelecionados: novos,
                                }));
                              }}
                            />
                            {dia}
                          </label>
                        );
                      })}
                    </div>

                    <div className="horarios">
                      <div>
                        <label>Horário de entrada *</label>
                        <input
                          type="time"
                          value={horario.fixo.entrada}
                          onChange={(e) =>
                            setHorario((prev) => ({
                              ...prev,
                              fixo: { ...prev.fixo, entrada: e.target.value },
                            }))
                          }
                          required
                          readOnly={isEstudanteReadOnly}
                          disabled={isEstudanteReadOnly}
                          style={
                            isEstudanteReadOnly
                              ? { backgroundColor: "#e2e8f0" }
                              : undefined
                          }
                        />
                      </div>
                      <div>
                        <label>Horário de saída *</label>
                        <input
                          type="time"
                          value={horario.fixo.saida}
                          onChange={(e) =>
                            setHorario((prev) => ({
                              ...prev,
                              fixo: { ...prev.fixo, saida: e.target.value },
                            }))
                          }
                          required
                          readOnly={isEstudanteReadOnly}
                          disabled={isEstudanteReadOnly}
                          style={
                            isEstudanteReadOnly
                              ? { backgroundColor: "#e2e8f0" }
                              : undefined
                          }
                        />
                      </div>
                      <div>
                        <label>Intervalo (opcional até 6h)</label>
                        <div className="intervalo-inputs">
                          <input
                            type="time"
                            value={horario.fixo.intervaloInicio}
                            onChange={(e) =>
                              setHorario((prev) => ({
                                ...prev,
                                fixo: {
                                  ...prev.fixo,
                                  intervaloInicio: e.target.value,
                                },
                              }))
                            }
                            readOnly={isEstudanteReadOnly}
                            disabled={isEstudanteReadOnly}
                            style={
                              isEstudanteReadOnly
                                ? { backgroundColor: "#e2e8f0" }
                                : undefined
                            }
                          />
                          <span> às </span>
                          <input
                            type="time"
                            value={horario.fixo.intervaloFim}
                            onChange={(e) =>
                              setHorario((prev) => ({
                                ...prev,
                                fixo: {
                                  ...prev.fixo,
                                  intervaloFim: e.target.value,
                                },
                              }))
                            }
                            readOnly={isEstudanteReadOnly}
                            disabled={isEstudanteReadOnly}
                            style={
                              isEstudanteReadOnly
                                ? { backgroundColor: "#e2e8f0" }
                                : undefined
                            }
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {horario.tipo === "alternado" && (
                <div className="horario-card tce-five-2">
                  <h4>
                    Horário Alternado{" "}
                    <small>(horários diferentes por dia)</small>
                  </h4>
                  <table className="horario-grade">
                    <thead>
                      <tr>
                        <th>Dia da semana</th>
                        <th>Horário de entrada</th>
                        <th>Horário de saída</th>
                        <th>Intervalo (opcional até 6h)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        { key: "segunda", label: "Segunda" },
                        { key: "terca", label: "Terça" },
                        { key: "quarta", label: "Quarta" },
                        { key: "quinta", label: "Quinta" },
                        { key: "sexta", label: "Sexta" },
                        { key: "sabado", label: "Sábado" },
                        { key: "domingo", label: "Domingo" },
                      ].map((dia) => (
                        <tr key={dia.key}>
                          <td>{dia.label}</td>
                          <td>
                            <input
                              type="time"
                              value={horario.alternado[dia.key].entrada}
                              onChange={(e) =>
                                setHorario((prev) => ({
                                  ...prev,
                                  alternado: {
                                    ...prev.alternado,
                                    [dia.key]: {
                                      ...prev.alternado[dia.key],
                                      entrada: e.target.value,
                                    },
                                  },
                                }))
                              }
                              readOnly={isEstudanteReadOnly}
                              disabled={isEstudanteReadOnly}
                              style={
                                isEstudanteReadOnly
                                  ? { backgroundColor: "#e2e8f0" }
                                  : undefined
                              }
                            />
                          </td>
                          <td>
                            <input
                              type="time"
                              value={horario.alternado[dia.key].saida}
                              onChange={(e) =>
                                setHorario((prev) => ({
                                  ...prev,
                                  alternado: {
                                    ...prev.alternado,
                                    [dia.key]: {
                                      ...prev.alternado[dia.key],
                                      saida: e.target.value,
                                    },
                                  },
                                }))
                              }
                              readOnly={isEstudanteReadOnly}
                              disabled={isEstudanteReadOnly}
                              style={
                                isEstudanteReadOnly
                                  ? { backgroundColor: "#e2e8f0" }
                                  : undefined
                              }
                            />
                          </td>
                          <td>
                            <div className="intervalo-inputs">
                              <input
                                type="time"
                                value={
                                  horario.alternado[dia.key].intervaloInicio
                                }
                                onChange={(e) =>
                                  setHorario((prev) => ({
                                    ...prev,
                                    alternado: {
                                      ...prev.alternado,
                                      [dia.key]: {
                                        ...prev.alternado[dia.key],
                                        intervaloInicio: e.target.value,
                                      },
                                    },
                                  }))
                                }
                                readOnly={isEstudanteReadOnly}
                                disabled={isEstudanteReadOnly}
                                style={
                                  isEstudanteReadOnly
                                    ? { backgroundColor: "#e2e8f0" }
                                    : undefined
                                }
                              />
                              <span> às </span>
                              <input
                                type="time"
                                value={horario.alternado[dia.key].intervaloFim}
                                onChange={(e) =>
                                  setHorario((prev) => ({
                                    ...prev,
                                    alternado: {
                                      ...prev.alternado,
                                      [dia.key]: {
                                        ...prev.alternado[dia.key],
                                        intervaloFim: e.target.value,
                                      },
                                    },
                                  }))
                                }
                                readOnly={isEstudanteReadOnly}
                                disabled={isEstudanteReadOnly}
                                style={
                                  isEstudanteReadOnly
                                    ? { backgroundColor: "#e2e8f0" }
                                    : undefined
                                }
                              />
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {horario.tipo && (
                <div className="carga-horaria-info">
                  {(() => {
                    const { errors, totalSemanal } = validarHorarios(horario);
                    return (
                      <>
                        {totalSemanal <= 30 ? (
                          <p className="carga-ok">
                            Carga horária semanal: {totalSemanal.toFixed(1)}
                            h/semana
                          </p>
                        ) : (
                          <p className="erro-carga">
                            Carga horária semanal: {totalSemanal.toFixed(1)}
                            h/semana ⚠️ Limite máximo permitido: 30h
                          </p>
                        )}
                        {errors.length > 0 && (
                          <ul className="horario-errors">
                            {errors.map((err, i) => (
                              <li key={i}>⚠️ {err}</li>
                            ))}
                          </ul>
                        )}
                      </>
                    );
                  })()}
                </div>
              )}

              <div className="div-fundo-txa">
                <div className="div-txa-obs-ajuste">
                  <label
                    className="textarea-observacoes-lbl"
                    htmlFor="atividades_desenvolvidas"
                  >
                    Atividades a serem desenvolvidas
                  </label>
                  <textarea
                    style={{
                      resize: "none",
                      backgroundColor: isEstudanteReadOnly
                        ? "#e2e8f0"
                        : undefined,
                    }}
                    className="textarea-observacoes"
                    htmlFor="atividades_desenvolvidas"
                    name="atividades_desenvolvidas"
                    id="textarea-observacoes"
                    placeholder="Digite aqui."
                    onChange={(e) => {
                      const regex = /[^0-9a-zA-ZÀ-ÿ\s.,&-]/g;
                      e.target.value = e.target.value.replace(regex, "");
                      handleChange(e);
                    }}
                    value={formData.atividades_desenvolvidas}
                    readOnly={isEstudanteReadOnly}
                    disabled={isEstudanteReadOnly}
                    title={
                      isEstudanteReadOnly
                        ? "Campo de responsabilidade da empresa."
                        : undefined
                    }
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="tce-card">
            <div className="div-dias-horarios-title tce-card-header">
              <label htmlFor="dias-horarios-title">
                Comprovante de Experiência do Supervisor de Estágio
              </label>
            </div>

            <div className="tce-card-body">
              <span id="cpv-id">
                Caso o supervisor não possua formação compatível com a área do
                curso do estudante, mas possua experiência comprovada na área de
                atuação, anexe abaixo o documento comprobatório.
              </span>
              <div className="div-incompatibilidade">
                <label className="checkbox-incompatibilidade">
                  <input
                    type="checkbox"
                    id="possui-incompatibilidade"
                    checked={possuiIncompatibilidade}
                    onChange={(e) =>
                      setPossuiIncompatibilidade(e.target.checked)
                    }
                  />
                  Supervisor possui formação <strong>incompatível</strong> com a
                  área do curso do estudante
                </label>
              </div>

              <div className="upload-supervisor">
                <label>
                  Documento comprobatório de experiência do supervisor
                  {possuiIncompatibilidade && (
                    <span style={{ color: "red" }}> *</span>
                  )}
                </label>
                <small>
                  {possuiIncompatibilidade
                    ? "Obrigatório: a formação do supervisor não é compatível com a área do curso."
                    : "Opcional: anexe apenas se necessário."}
                </small>

                <div className="upload-box">
                  <div className="upload-info">
                    <strong>Selecione um arquivo</strong>
                    <span> ou arraste e solte aqui</span>
                    <small>
                      Formatos permitidos: PDF, JPG, JPEG, PNG
                      <br />
                      Tamanho máximo: 10MB
                    </small>
                    <input
                      id="arquivo-supervisor"
                      ref={inputSupervisorRef}
                      type="file"
                      accept=".pdf,.png,.jpg,.jpeg"
                      onChange={handleArquivoSupervisor}
                      hidden
                    />
                    <label htmlFor="arquivo-supervisor" className="btn-upload">
                      Selecionar arquivo
                    </label>
                  </div>
                </div>

                {arquivoSupervisor && (
                  <>
                    <h4 style={{ marginTop: "25px" }}>Arquivo anexado</h4>
                    <div className="arquivo-enviado">
                      <div className="arquivo-info">
                        <strong>{arquivoSupervisor.name}</strong>
                        <span>
                          {(arquivoSupervisor.size / 1024).toFixed(0)} KB
                        </span>
                      </div>

                      <div className="arquivo-acoes">
                        <button
                          type="button"
                          onClick={visualizarArquivoSupervisor}
                        >
                          Visualizar
                        </button>
                        <button
                          type="button"
                          onClick={removerArquivoSupervisor}
                        >
                          Remover
                        </button>
                      </div>
                    </div>

                    <div className="info-final">
                      <strong>Importante:</strong> O documento anexado será
                      encaminhado para análise da equipe do CIEEMG.
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="tce-card">
          <div className="div-dados-escolares-title tce-card-header">
            <label htmlFor="dados-escolares-title">Dados Escolares</label>
          </div>

          <div className="tce-card-body">
            <div className="tce-eight">
              <div className="tce-eight-1">
                <div className="div-escola-faculdade">
                  <label className="all-label" htmlFor="escola">
                    Escola/Faculdade
                  </label>
                  <input
                    type="text"
                    name="escola"
                    className="escola-faculdade"
                    id="escola-faculdade"
                    disabled={isEstudanteDisabled}
                    onChange={handleChange}
                    value={formData.escola}
                  />
                </div>

                <div className="div-unidade">
                  <label className="all-label" htmlFor="unidade">
                    Unidade
                  </label>
                  <input
                    type="text"
                    name="unidade"
                    className="tce-eight-unidade"
                    id="tce-eight-unidade"
                    maxLength={100}
                    disabled={isEstudanteDisabled}
                    onChange={handleChange}
                    value={formData.unidade}
                  />
                </div>

                <div className="div-curso">
                  <label className="all-label" htmlFor="curso">
                    Curso
                  </label>
                  <input
                    type="text"
                    name="curso"
                    className="tce-eight-curso"
                    id="tce-eight-curso"
                    maxLength={100}
                    disabled={isEstudanteDisabled}
                    onChange={handleChange}
                    value={formData.curso}
                  />
                </div>
              </div>

              <div className="tce-eight-2">
                <div className="div-ano-periodo-serie">
                  <label className="all-label" htmlFor="ano_periodo_serie">
                    Ano, período ou série
                  </label>
                  <input
                    type="text"
                    name="ano_periodo_serie"
                    className="ano-periodo-serie"
                    id="ano_periodo_serie"
                    disabled={isEstudanteDisabled}
                    onChange={handleChange}
                    value={formData.ano_periodo_serie}
                  />
                </div>

                <div className="div-previsao-formatura">
                  <label className="all-label" htmlFor="previsao_formatura">
                    Previsão de Formatura
                  </label>
                  <input
                    type="text"
                    name="previsao_formatura"
                    className="previsao-formatura"
                    id="tce_previsao_formatura"
                    disabled={isEstudanteDisabled}
                    onChange={handleChange}
                    value={formData.previsao_formatura}
                  />
                </div>

                <div className="div-tipo-estagio">
                  <label className="all-label" htmlFor="tce-eight-tipo-estagio">
                    Tipo de estágio é obrigatório?
                  </label>
                  <select
                    name="tipo_estagio"
                    id="tipo_estagio"
                    onChange={handleChange}
                    disabled={isEstudanteDisabled}
                    value={formData.tipo_estagio ?? ""}
                  >
                    <option value="" disabled>
                      Selecione
                    </option>
                    <option value="S">Sim</option>
                    <option value="N">Não</option>
                  </select>
                </div>
              </div>

              <div className="tce-eight-3">
                <div className="div-orientador-estagio" id="orientador-estagio">
                  <label className="all-label" htmlFor="orientador_estagio">
                    Orientador de Estágio / Responsável pelo Estágio /
                    Coordenador de Estágio ou de Curso (Escola/Faculdade)
                  </label>
                  <input
                    type="text"
                    name="orientador_estagio"
                    className="orientador-estagio"
                    id="orientador-estagio"
                    onChange={handleChange}
                    value={formData.orientador_estagio}
                    disabled={isEstudanteDisabled}
                  />
                </div>
              </div>

              <div className="tce-eight-4">
                <div className="div-cargo">
                  <label className="all-label" htmlFor="cargo">
                    Cargo
                  </label>
                  <input
                    type="text"
                    name="cargo"
                    className="ano-periodo-serie"
                    id="cargo"
                    onChange={handleChange}
                    value={formData.cargo}
                    disabled={isEstudanteDisabled}
                  />
                </div>

                <div className="div-tce-eight-email">
                  <label className="all-label" htmlFor="email_escola">
                    E-mail
                  </label>
                  <input
                    type="email"
                    name="email_escola"
                    className="tce-eight-email"
                    id="email_escola"
                    onChange={handleChange}
                    value={formData.email_escola}
                    disabled={isEstudanteDisabled}
                  />
                </div>

                <div className="div-tce-eight-cnpj" id="tce-eight-cnpj">
                  <label className="all-label" htmlFor="cnpj_escola">
                    CNPJ
                  </label>
                  <InputMask
                    mask="999.999/9999-999"
                    maskChar=" "
                    type="text"
                    name="cnpj_escola"
                    className="tce-eight-cnpj"
                    id="tce-eight-cnpj"
                    onChange={handleChange}
                    value={formData.cnpj_escola}
                    disabled={isEstudanteDisabled}
                    required
                  />
                </div>
              </div>

              <label className="lbl-tce-eight-observacoes">
                Observações: <br />
                1) O estudante não deve iniciar o estágio antes da assinatura do
                TCE; <br />
                2) Solicitar ao estudante uma declaração de matrícula e
                frequência emitida pela escola; <br />
                3) Em casos de alteração de endereço da empresa, favor informar.
              </label>
            </div>
          </div>
        </div>

        <div style={{ padding: "0 1rem 1rem 1rem", boxSizing: "border-box" }}>
          <div
            className="tce-nine-1"
            style={{ marginTop: "0.25rem", textAlign: "center" }}
          >
            <label htmlFor="" style={{ color: "#fff" }}>
              Em caso de dúvidas ou necessidade de mais orientações ligue para o
              telefone/Whatsapp: (31)3429-8100 ou envie um e-mail para
              atendimento@cieemg.org.br
            </label>
          </div>
        </div>

        <div className="tce-card">
          <div className="div-orientacao-imposto-title tce-card-header">
            <label htmlFor="ajuste-emissao-contrato-title">
              Atenção - Orientações sobre o imposto de renda para bolsas
              liberadas via CIEE/MG
            </label>
          </div>

          <div className="tce-card-body">
            <div className="div-tce-ten">
              <div className="div-tce-ten-1">
                <img src="./imagem-incidencia-mensal.png" alt="" />
                <div className="text-container">
                  <span className="span-1">
                    Conforme as regras de retenção de Imposto de Renda vigentes,
                    empresas em que o CIEE/MG realiza o repasse dos valores de
                    Bolsa - Auxílio, o teto máximo que o CIEE/MG pode liberar
                    por estagiário não pode ultrapassar o valor total de
                    R$2.824,00
                  </span>
                  <span className="span-2">
                    Caso o valor total de todos os valores liberados para o
                    estagiário ultrapasse esse teto, a retenção de Imposto de
                    Renda deve ser realizado pela liberação do recurso para
                    pagamento. *Manual eSocial *Medida Provisória nº 1206, de 06
                    de fevereiro de 2024 Lei nº 14.663, de 28 de Agosto de 2023
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <DocumentUploadForm
          schema={ANEXOS_SCHEMA_TCE}
          filesById={anexosFiles}
          statusById={anexosStatus}
          highlightIds={anexosObrigatoriosDestacar}
          onAnexar={(id, file) =>
            handleAnexoChange(id)(file)
          }
          disabled={!isEstudante}
          topNote={
            isEstudante
              ? "Gentileza anexar sua declaração escolar atualizada ou com no máximo 30 dias de emissão. Sem a declaração escolar sua contratação não será efetivada."
              : "Anexos de responsabilidade do estudante. Apenas o estudante pode anexar os documentos obrigatórios (declaração escolar, declaração de carga horária e documento de identificação). O envio é feito na área de edição do aprendiz."
          }
          missingMessage={
            isEstudante
              ? "Anexe a declaração escolar atualizada, com no máximo 30 dias de emissão. Sem ela, sua contratação não será efetivada."
              : ""
          }
          observations={formData.observacoes2}
          insertObservations={showObs2}
          onInsertObservationsChange={(v) => {
            setShowObs2(v);
            setFormData((p) => ({
              ...p,
              observacoes2: v ? p.observacoes2 : "",
            }));
          }}
          onObservationsChange={(v) => {
            const regex = /[^0-9a-zA-ZÀ-ÿ\s.,&-]/g;
            const filtered = String(v).replace(regex, "");
            setFormData((p) => ({ ...p, observacoes2: filtered }));
          }}
        />

        {/* === CIEE/MG - Particularidades para emissão do contrato === */}
        {/* Movido para o final do formulário e edição restrita ao perfil SEOPE. */}
        <div className="tce-card">
          <div className="tce-card-body">
            <div
              className="div-observacoes"
              style={{
                background: "rgba(30, 41, 59, 0.55)",
                borderRadius: 10,
                padding: "16px 18px",
                display: "flex",
                flexDirection: "column",
                gap: 12,
                border: "1px solid rgba(255, 255, 255, 0.06)",
                boxSizing: "border-box",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  justifyContent: "space-between",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div
                    style={{
                      width: 4,
                      height: 16,
                      borderRadius: 2,
                      background: "#185fa5",
                    }}
                  />
                  <p
                    style={{
                      fontSize: 13,
                      fontWeight: 600,
                      margin: 0,
                      color: "#fff",
                      letterSpacing: 0.2,
                    }}
                  >
                    CIEE/MG - Particularidades para emissão do contrato
                  </p>
                </div>
                {nivel !== 2 && (
                  <span
                    title="Somente o perfil SEOPE pode editar este bloco"
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      padding: "4px 10px",
                      borderRadius: 12,
                      background: "rgba(148, 163, 184, 0.18)",
                      color: "#cbd5e1",
                      border: "1px solid rgba(148, 163, 184, 0.35)",
                      letterSpacing: 0.4,
                    }}
                  >
                    SOMENTE SEOPE
                  </span>
                )}
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                  gap: 8,
                }}
              >
                <button
                  type="button"
                  disabled={nivel !== 2}
                  onClick={() => {
                    handleChange({
                      target: { name: "observacoes1", value: "inserir" },
                    });
                    setShowObs1(true);
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 6,
                    padding: "0.65rem 1rem",
                    borderRadius: 8,
                    border: showObs1
                      ? "1px solid #01336d"
                      : "1px solid rgba(255, 255, 255, 0.18)",
                    background: showObs1
                      ? "#01336d"
                      : "rgba(255, 255, 255, 0.04)",
                    color: showObs1 ? "#ffffff" : "#e7e5e4",
                    fontSize: 13,
                    fontWeight: 600,
                    letterSpacing: 0.3,
                    cursor: nivel === 2 ? "pointer" : "not-allowed",
                    opacity: nivel === 2 ? 1 : 0.5,
                    transition:
                      "background-color 0.2s ease, box-shadow 0.2s ease, transform 0.1s ease",
                  }}
                >
                  {showObs1 && <Check size={13} />}
                  Inserir Particularidades para emissão do contrato
                </button>
                <button
                  type="button"
                  disabled={nivel !== 2}
                  onClick={() => {
                    handleChange({
                      target: { name: "observacoes1", value: "nao-inserir" },
                    });
                    setShowObs1(false);
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 6,
                    padding: "0.65rem 1rem",
                    borderRadius: 8,
                    border: !showObs1
                      ? "1px solid #01336d"
                      : "1px solid rgba(255, 255, 255, 0.18)",
                    background: !showObs1
                      ? "#01336d"
                      : "rgba(255, 255, 255, 0.04)",
                    color: !showObs1 ? "#ffffff" : "#e7e5e4",
                    fontSize: 13,
                    fontWeight: 600,
                    letterSpacing: 0.3,
                    cursor: nivel === 2 ? "pointer" : "not-allowed",
                    opacity: nivel === 2 ? 1 : 0.5,
                    transition:
                      "background-color 0.2s ease, box-shadow 0.2s ease, transform 0.1s ease",
                  }}
                >
                  {!showObs1 && <Check size={13} />}
                  Não Inserir particularidades para emissão do contrato
                </button>
              </div>

              <div className="div-txa-obs-ajuste-observacoes1">
                {showObs1 && (
                  <textarea
                    style={{
                      width: "100%",
                      boxSizing: "border-box",
                      borderRadius: 8,
                      border: "1px solid #cbd5e1",
                      padding: 10,
                      fontSize: 13,
                      fontFamily: "inherit",
                      resize: "none",
                      background: "#ffffff",
                      color: "#0f172a",
                      outline: "none",
                      transition: "border-color 0.18s ease",
                    }}
                    className="textarea-observacoes"
                    name="observacoes1"
                    placeholder="Digite aqui."
                    disabled={nivel !== 2}
                    onChange={(e) => {
                      if (nivel !== 2) return;
                      const regex = /[^0-9a-zA-ZÀ-ÿ\s.,&-]/g;
                      e.target.value = e.target.value.replace(regex, "");
                      handleChange(e);
                    }}
                    value={
                      formData.observacoes1 === "inserir"
                        ? ""
                        : formData.observacoes1
                    }
                  />
                )}
              </div>
            </div>
          </div>
        </div>

        {modoEdicao && tceHistorico.length > 0 && (
          <div className="tca-card tca-mb-4">
            <div className="tca-card-header">Histórico de Transições</div>
            <div className="tca-card-body">
              <ul className="tca-historico-list">
                {tceHistorico.map((h, i) => {
                  const corAnterior = getStatusCor(
                    h.status_anterior || "início",
                  );
                  const corNovo = getStatusCor(h.status_novo);

                  return (
                    <li key={i} className="tca-historico-item">
                      <div className="tca-historico-status">
                        <span
                          className="tca-historico-badge"
                          style={{ background: corAnterior, color: "#fff" }}
                        >
                          {h.status_anterior || "início"}
                        </span>
                        <span className="tca-historico-seta">→</span>
                        <span
                          className="tca-historico-badge"
                          style={{ background: corNovo, color: "#fff" }}
                        >
                          {h.status_novo}
                        </span>
                      </div>

                      <small className="tca-historico-info">
                        Por: {h.usuario_nome || h.usuario_id} ({h.usuario_tipo})
                      </small>
                      <small className="tca-historico-info">
                        {new Date(h.created_at).toLocaleString("pt-BR")}
                      </small>

                      {h.observacao && (
                        <div className="tca-historico-observacao">
                          📝 {h.observacao}
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        )}

        <div className="btn-wrapper">
          {!modoEdicao && (
            <button
              type="submit"
              className="btn-cadastrar-tce"
              disabled={
                !permissao.podeEditarEmpresa && !permissao.podeEditarEstudante
              }
            >
              Cadastrar
            </button>
          )}

          {modoEdicao && (
            <>
              {tceStatus === "AGUARDANDO_ESTUDANTE" &&
                permissao.podeSubmeter && (
                  <button onClick={submeterValidacaoEstudante}>
                    Submeter para CIEEMG
                  </button>
                )}

              {(["EM_VALIDACAO", "PENDENTE"].includes(tceStatus)) &&
                permissao.podeAprovar && (
                  <>
                    <button onClick={aprovar} className="btn-aprovar">
                      Aprovar
                    </button>
                    {permissao.podeReprovar && (
                      <button onClick={recusar} className="btn-recusar">
                        Reprovar
                      </button>
                    )}
                  </>
                )}

              {tceStatus === "AGUARDANDO_CONFECCAO" &&
                permissao.podeAprovar && (
                  <button
                    type="button"
                    onClick={marcarConfeccionado}
                    className="btn-cadastrar-tce"
                  >
                    Marcar Contrato como Confeccionado
                  </button>
                )}

              {tceStatus === "CONTRATO_CONFECCIONADO" &&
                permissao.podeAprovar && (
                  <button
                    type="button"
                    onClick={marcarDisponibilizado}
                    className="btn-cadastrar-tce"
                  >
                    Marcar como Disponibilizado para Assinatura
                  </button>
                )}

              {tceStatus === "AGUARDANDO_ASSINATURAS" &&
                permissao.podeAprovar && (
                  <button
                    type="button"
                    onClick={finalizar}
                    className="btn-aprovar"
                  >
                    Finalizar Processo
                  </button>
                )}


              {!tceStatus &&
                (permissao.podeReprovar || permissao.podeAprovar) && (
                  <span
                    style={{
                      fontSize: 12,
                      color: "#fbbf24",
                      alignSelf: "center",
                    }}
                  >
                    ⚠ Status não retornado pelo backend. Ações indisponíveis.
                  </span>
                )}
            </>
          )}
        </div>

        {showReview && (
          <div
            className="tca-modal-overlay"
            onClick={(e) => {
              if (e.target === e.currentTarget) setShowReview(false);
            }}
            role="dialog"
            aria-modal="true"
          >
            <div className="tca-modal-content">
              <FormReview
                data={buildReviewData()}
                onEdit={() => setShowReview(false)}
                onSubmit={handleConfirmSubmit}
                confirmMessage="Tem certeza que deseja enviar o TCE?"
              />
            </div>
          </div>
        )}
      </form>

      <ToastContainer toasts={toasts} onRemove={removeToast} />
      {confirmDialog}

      <MissingFieldsAlert
        missingFields={missingFieldsAlert}
        onFieldClick={handleMissingFieldClick}
        onClose={() => setMissingFieldsAlert([])}
      />

      <SuccessSubmitModal
        isOpen={successModalData.isOpen}
        onClose={() => {
          setSuccessModalData({ ...successModalData, isOpen: false });
          window.location.reload();
        }}
        formId={successModalData.tceId}
        role={successModalData.role}
        formType="TCE"
      />
    </>
  );
};
const TCEWrapper = () => {
  const currentUser = JSON.parse(localStorage.getItem("user"));
  const isEstudante = Number(currentUser?.acesso_autorizacao ?? 0) === 1;
  const idNaUrl =
    typeof window !== "undefined"
      ? new URLSearchParams(window.location.search).get("id")
      : null;

  // Estudante só cai na tela "Nenhuma autorização vinculada" se NÃO
  // tiver id na URL E TAMBÉM não tiver fallback no localStorage. Isso
  // cobre o caso onde o /tce/login-estudante retorna tce_id mas por
  // alguma race condition o navigate vai para /tce sem ?id=.
  if (isEstudante && !idNaUrl) {
    let hasFallback = false;
    try {
      const stored = localStorage.getItem('estudante_tce_id');
      hasFallback = !!stored && /^\d+$/.test(String(stored).trim());
    } catch (_) {
      hasFallback = false;
    }
    if (!hasFallback) {
      return <AguardandoEmpresa />;
    }
  }
  return <TCE />;
};

export default TCEWrapper;
